import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const TIPOS_USUARIO = [
  { value:'MEDICO',        label:'Médico',          icon:'🩺', color:'var(--navy-lt)' },
  { value:'SECRETARIA',    label:'Secretaria',      icon:'💼', color:'var(--green)' },
  { value:'ADMINISTRADOR', label:'Administrador',   icon:'⚙️', color:'#1a2535' },
  { value:'ENFERMERIA',    label:'Enfermería',      icon:'💉', color:'#0891B2' },
  { value:'LABORATORIO',   label:'Laboratorio',     icon:'🔬', color:'#7C3AED' },
  { value:'OTRO',          label:'Otro',            icon:'👤', color:'#6B7280' },
];

const CHECKS_CONFIG = [
  { key:'CONTABILIDAD',   label:'Obligado a llevar contabilidad' },
  { key:'CONTRIBUYENTE',  label:'Contribuyente especial' },
  { key:'BLOQUEO_PAGO',  label:'Bloqueo forma de pago' },
  { key:'CONVENIO_SUC',  label:'Convenios por sucursal' },
  { key:'VADEMECUM',      label:'Usar varios vademécums' },
  { key:'CTRL_FECHAS',   label:'Control rango de fechas' },
  { key:'DESC_100',      label:'Descuento 100%' },
  { key:'GEN_DOCS',      label:'Generar documentos' },
];

const COLORS_CONFIG = [
  { key:'--navy',    label:'Color principal',    default:'#1a2535' },
  { key:'--accent',  label:'Color acento',        default:'#2e6da4' },
  { key:'--bg',      label:'Fondo general',       default:'#f0f4f8' },
  { key:'--text',    label:'Texto principal',     default:'#1a2535' },
  { key:'--border',  label:'Bordes',              default:'#dde3ec' },
  { key:'--green',   label:'Éxito',               default:'#1a7a4a' },
  { key:'--amber',   label:'Advertencia',         default:'#b85c00' },
  { key:'--red',     label:'Peligro',             default:'#b03030' },
];

const STORAGE_KEY = 'medisys_config';
const loadConfig  = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY))||{}; } catch { return {}; } };
const saveConfig  = cfg => localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
const applyColors = colors => { const r=document.documentElement; Object.entries(colors).forEach(([k,v])=>r.style.setProperty(k,v)); };

const tipoInfo = v => TIPOS_USUARIO.find(t=>t.value===v)||TIPOS_USUARIO[5];

const FORM_EMPTY = { USUARIO:'', NOMBRES:'', PWD:'', CEDULA:'', TIPO_USUARIO:'MEDICO',
  ESPECIALIDAD:'', REGISTRO_MSP:'', SENESCYT:'', EMAIL:'', TELEFONO:'', DIRECCION:'',
  SRI_RUC:'', SRI_RAZON_SOCIAL:'', SRI_DIRECCION:'', SRI_ESTAB:'', SRI_PTO_EMI:'' };

const CATEGORIAS_FACTURACION_DEFAULT = [
  { value:'TUR',  label:'Turnos / Consulta' },
  { value:'LAB',  label:'Laboratorio' },
  { value:'FAR',  label:'Farmacia' },
  { value:'OTRO', label:'Otro' },
];

export default function Configuracion() {
  const { usuario } = useAuth();
  const esAdmin = usuario?.esDBA || ['ADMINISTRADOR'].includes((usuario?.tipo_usuario||'').toUpperCase());
  const esSecretaria = (usuario?.tipo_usuario||'').toUpperCase() === 'SECRETARIA' && !usuario?.esDBA;

  const [tab, setTab]         = useState('usuarios');
  const [tarifario, setTarifario] = useState([]);
  const [filtroServ, setFiltroServ] = useState('');
  const [filtroTipoServ, setFiltroTipoServ] = useState('');
  const [plantillasConsent, setPlantillasConsent] = useState([]);
  const [showFormConsent, setShowFormConsent] = useState(false);
  const [editConsentId, setEditConsentId] = useState(null);
  const [formConsent, setFormConsent] = useState({TITULO:'',CUERPO:'',REQUIERE_FIRMA_MEDICO:false,REQUIERE_FIRMA_TESTIGO:false});
  const [savingConsent, setSavingConsent] = useState(false);
  const [showNuevoServ, setShowNuevoServ] = useState(false);
  const [nuevoServ, setNuevoServ] = useState({SERVICIO:'',VALOR:0,APLICA_IVA:false,VALOR_MODIFICABLE:true,COD_TIPO_SERVICIO:''});
  const [savingServ, setSavingServ] = useState(false);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [ivaGlobal, setIvaGlobal] = useState(15);
  const [savingTar, setSavingTar]   = useState(false);
  const [iaCfg,     setIaCfg]       = useState({api_key:'',activo:false,configurado:false});
  const [savingIA,  setSavingIA]    = useState(false);
  const [showKey,   setShowKey]     = useState(false);
  const [plantillasC, setPlantillasC] = useState([]);
  const [showFormPl,  setShowFormPl]  = useState(false);
  const [editPl,      setEditPl]      = useState(null);
  const [formPl, setFormPl] = useState({NOMBRE:'',DESCRIPCION:'',EVOLUCION:'',diagnosticos:[]});
  const [busqCie, setBusqCie] = useState('');
  const [sugsCie, setSugsCie] = useState([]);
  const [grupos,    setGrupos]    = useState([]);
  const [examList,  setExamList]  = useState([]);
  const [grupoSel,  setGrupoSel]  = useState('');
  const [examExp,   setExamExp]   = useState(null);
  const [extDatos,  setExtDatos]  = useState({});
  const [extSaving, setExtSaving] = useState(false);
  const [showFormEx, setShowFormEx] = useState(false);
  const [formEx,    setFormEx]    = useState({DETERMINACION:'',COD_TIPO_DETERMINACION:'',COD_SERVICIO:'',ORDEN_PRESENTACION:1});
  const [showFormGrupo, setShowFormGrupo] = useState(false);
  const [formGrupo, setFormGrupo] = useState({TIPO_DETERMINACION:'',C_FORMULARIO:'CLN',ORDEN_PRESENTACION:1});
  const [savingGrupo, setSavingGrupo] = useState(false);
  const [editandoGrupoId, setEditandoGrupoId] = useState(null);
  const [filtroGrupo, setFiltroGrupo] = useState('');

  /* Variables de laboratorio: subdeterminaciones, rangos de normalidad, rangos categóricos, unidades */
  const [labCompleto, setLabCompleto] = useState(null);
  const [catalogoSubdet, setCatalogoSubdet] = useState([]);
  const [unidadesLab, setUnidadesLab] = useState([]);
  const [showNuevaSub, setShowNuevaSub] = useState(false);
  const [nuevaSub, setNuevaSub] = useState({SUBDETERMINACION:'',SECCION:''});
  const [subExistente, setSubExistente] = useState('');
  const [showFormRango, setShowFormRango] = useState(false);
  const [rangoTarget, setRangoTarget] = useState(null);
  const [formRango, setFormRango] = useState({C_SEXO:'I',EDAD_INFERIOR:0,EDAD_SUPERIOR:150,LIMITE_INFERIOR:0,LIMITE_SUPERIOR:0,COD_UNIDAD:'',OBSERVACIONES:''});
  const [showNuevaUnidadEnRango, setShowNuevaUnidadEnRango] = useState(false);
  const [nuevaUnidad, setNuevaUnidad] = useState({C_AGRUPADOR:'GEN',UNIDAD:'',DESCRIPCION:''});
  const [showFormCat, setShowFormCat] = useState(false);
  const [formCat, setFormCat] = useState({ETIQUETA:'',LIMITE_INFERIOR:0,LIMITE_SUPERIOR:0,COLOR:'#888888',C_SEXO:'I',EDAD_INFERIOR:0,EDAD_SUPERIOR:150});
  const [savingLabVar, setSavingLabVar] = useState(false);
  const SEXO_LABEL = {M:'Masc.',F:'Fem.',I:'Ambos'};
  const [config, setConfig]   = useState(loadConfig);
  const [empresa, setEmpresa] = useState({
    RUC: config.empresa?.RUC||'',
    RAZON_SOCIAL: config.empresa?.RAZON_SOCIAL||'',
    NOMBRE_COMERCIAL: config.empresa?.NOMBRE_COMERCIAL||'',
    REPRESENTANTE: config.empresa?.REPRESENTANTE||'',
    DIRECCION: config.empresa?.DIRECCION||'',
    TELEFONO: config.empresa?.TELEFONO||'',
    EMAIL: config.empresa?.EMAIL||'',
    CODIGO_INEC: config.empresa?.CODIGO_INEC||'',
    TIPO_ESTABLECIMIENTO: config.empresa?.TIPO_ESTABLECIMIENTO||'Consultorio Médico',
    NIVEL_ATENCION: config.empresa?.NIVEL_ATENCION||'Primer Nivel',
    PROVINCIA: config.empresa?.PROVINCIA||'',
    CANTON: config.empresa?.CANTON||'',
    PARROQUIA: config.empresa?.PARROQUIA||'',
    DIRECTOR_MEDICO: config.empresa?.DIRECTOR_MEDICO||'',
    CARGO_DIRECTOR: config.empresa?.CARGO_DIRECTOR||'Director/a Médico/a',
    REGISTRO_MSP_DIR: config.empresa?.REGISTRO_MSP_DIR||'',
    HORAS_EDICION: config.empresa?.HORAS_EDICION||72,
    POLITICA_PRIVACIDAD_HC: config.empresa?.POLITICA_PRIVACIDAD_HC||'RESTRINGIDA',
  });
  const [checks, setChecks]   = useState(config.checks||{});
  const [colors, setColors]   = useState(config.colors||Object.fromEntries(COLORS_CONFIG.map(c=>[c.key,c.default])));
  const [logo, setLogo]       = useState(config.logo||null);
  const [toast, setToast]     = useState('');

  /* Usuarios */
  const [usuarios, setUsuarios]   = useState([]);
  const [filtro, setFiltro]       = useState('');
  const [filtroTipo, setFTipo]    = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [form, setForm]           = useState(FORM_EMPTY);
  const [saving, setSaving]       = useState(false);
  const [errMsg, setErrMsg]       = useState('');
  const [firmaInfo, setFirmaInfo] = useState({});
  const [p12Pass, setP12Pass]     = useState('');
  const [firmaTexto, setFirmaTxt] = useState('');
  const [subiendo, setSubiendo]   = useState('');
  const [facturaRucPropio, setFacturaRucPropio] = useState(false);
  const logoRef = useRef();

  /* Facturación electrónica SRI */
  const [sriCentro, setSriCentro] = useState({ SRI_ESTAB:'001', SRI_PTO_EMI:'001', SRI_AMBIENTE:'1', TIENE_P12_CENTRO:false });
  const [savingSri, setSavingSri] = useState(false);
  const [p12CentroPass, setP12CentroPass] = useState('');
  const [subiendoP12Centro, setSubiendoP12Centro] = useState(false);
  const [puntos, setPuntos] = useState({});
  const [categoriasFact, setCategoriasFact] = useState(CATEGORIAS_FACTURACION_DEFAULT);
  const [savingPuntos, setSavingPuntos] = useState(false);
  const p12CentroRef = useRef();

  useEffect(()=>{ cargarUsuarios(); },[]);
  useEffect(()=>{
    if(tab==='ia'){
      api.get('/historia/ia-config').then(r=>setIaCfg(c=>({...c,...r.data}))).catch(()=>{});
    }
    if(tab==='plantillas'){
      api.get(`/historia/plantillas-consulta?medico=${usuario?.usuario}`)
        .then(r=>setPlantillasC(r.data||[])).catch(()=>{});
    }
    if(tab==='consentimientos'){
      api.get('/historia/plantillas-consentimiento')
        .then(r=>setPlantillasConsent(r.data||[])).catch(()=>{});
    }
    if(tab==='examenes'){
      api.get('/laboratorio/config/grupos').then(r=>setGrupos(r.data||[])).catch(()=>{});
      api.get('/laboratorio/config/subdeterminaciones').then(r=>setCatalogoSubdet(r.data||[])).catch(()=>{});
      api.get('/laboratorio/config/unidades').then(r=>setUnidadesLab(r.data||[])).catch(()=>{});
    }
    if(tab==='tarifario'){
      api.get('/configuracion/tarifario')
        .then(r=>{ setTarifario(r.data.servicios||[]); setIvaGlobal(r.data.iva_global||15); })
        .catch(()=>{});
      api.get('/configuracion/tipos-servicio').then(r=>setTiposServicio(r.data||[])).catch(()=>{});
    }
    if(tab==='general'){
      api.get('/configuracion/ms-config')
        .then(r=>{
          if(r.data && Object.keys(r.data).length > 0){
            setEmpresa(ep=>({...ep,...r.data}));
          }
        })
        .catch(()=>{});
    }
    if(tab==='estilos'){
      api.get('/configuracion/ms-config')
        .then(r=>{
          if(r.data?.LOGO_PATH){
            const base=(process.env.REACT_APP_API_URL||'').replace('/api','').replace(/\/$/,'');
            setLogo(`${base}${r.data.LOGO_PATH}`);
          }
        })
        .catch(()=>{});
    }
    if(tab==='facturacion'){
      api.get('/configuracion/ms-config')
        .then(r=>{
          if(r.data){
            setSriCentro(s=>({...s,
              SRI_ESTAB:    r.data.SRI_ESTAB    || '001',
              SRI_PTO_EMI:  r.data.SRI_PTO_EMI  || '001',
              SRI_AMBIENTE: r.data.SRI_AMBIENTE || '1',
              TIENE_P12_CENTRO: !!r.data.TIENE_P12_CENTRO,
            }));
          }
        })
        .catch(()=>{});
      api.get('/configuracion/puntos-facturacion')
        .then(r=>{
          setPuntos(r.data?.puntos||{});
          if(r.data?.categorias?.length) setCategoriasFact(r.data.categorias);
        })
        .catch(()=>{});
    }
  },[tab]);
  useEffect(()=>{ applyColors(colors); },[colors]);

  const cargarUsuarios = async () => {
    try{
      const{data}=await api.get('/configuracion/usuarios');
      setUsuarios(data);
    }catch(e){
      showToast('✗ No se pudo cargar la lista de usuarios: ' + (e.response?.data?.error||e.message));
    }
  };

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2500); };

  const abrirNuevo = () => {
    setEditUser(null); setForm(FORM_EMPTY); setErrMsg(''); setShowModal(true);
  };

  const abrirEditar = async u => {
    setEditUser(u.USUARIO);
    setForm({ USUARIO:u.USUARIO, NOMBRES:u.NOMBRES||'', PWD:'', CEDULA:u.CEDULA||'',
      TIPO_USUARIO:u.TIPO_USUARIO||'MEDICO', ESPECIALIDAD:u.ESPECIALIDAD||'',
      REGISTRO_MSP:u.REGISTRO_MSP||'', SENESCYT:u.SENESCYT||'',
      EMAIL:u.EMAIL||'', TELEFONO:u.TELEFONO||'', DIRECCION:u.DIRECCION||'',
      SRI_RUC:'', SRI_RAZON_SOCIAL:'', SRI_DIRECCION:'', SRI_ESTAB:'', SRI_PTO_EMI:'' });
    setErrMsg(''); setP12Pass(''); setSubiendo(''); setFacturaRucPropio(false);
    setShowModal(true);
    try {
      const{data}=await api.get(`/configuracion/usuarios/${u.USUARIO}/p12-info`);
      setFirmaInfo(data);
      setFirmaTxt(data.firma_texto||'');
    } catch { setFirmaInfo({}); }
    // Datos SRI completos (el listado no trae el RUC completo, solo si tiene o no)
    try {
      const{data}=await api.get(`/configuracion/usuarios/${u.USUARIO}`);
      setForm(f=>({...f,
        SRI_RUC: data.SRI_RUC||'', SRI_RAZON_SOCIAL: data.SRI_RAZON_SOCIAL||'',
        SRI_DIRECCION: data.SRI_DIRECCION||'', SRI_ESTAB: data.SRI_ESTAB||'',
        SRI_PTO_EMI: data.SRI_PTO_EMI||'' }));
      setFacturaRucPropio(!!data.SRI_RUC);
    } catch { /* si falla, se deja el toggle apagado por defecto */ }
  };

  const guardarUsuario = async () => {
    if(!form.NOMBRES.trim()||(!editUser&&!form.USUARIO.trim())||(!editUser&&!form.PWD.trim())){
      setErrMsg('Usuario, nombre y contraseña son obligatorios'); return;
    }
    setSaving(true); setErrMsg('');
    // Si el toggle "factura con RUC propio" está apagado, el RUC se envía vacío
    // sin importar lo que haya quedado tipeado — así es como se desactiva
    // (ver facturaVaAlCentro() en facturacion.js: RUC vacío = factura al Centro).
    const payload = { ...form, SRI_RUC: facturaRucPropio ? form.SRI_RUC.trim() : '' };
    try{
      if(editUser){
        await api.put(`/configuracion/usuarios/${editUser}`, payload);
      } else {
        await api.post('/configuracion/usuarios', payload);
      }
      setShowModal(false); cargarUsuarios();
      showToast(editUser ? '✓ Usuario actualizado' : '✓ Usuario creado');
    }catch(e){ setErrMsg(e.response?.data?.error||e.message); }
    finally{ setSaving(false); }
  };

  const toggleActivo = async (usuario, activo) => {
    try{
      await api.patch(`/configuracion/usuarios/${usuario}/toggle`);
      cargarUsuarios();
      showToast(activo ? 'Usuario desactivado' : 'Usuario activado');
    }catch{}
  };

  const guardarGeneral = async () => {
    const cfg = { empresa, checks, colors, logo };
    applyColors(colors);
    // Guardar también en BD
    try{
      await api.put('/configuracion/ms-config', {
        RUC: empresa.RUC, RAZON_SOCIAL: empresa.RAZON_SOCIAL,
        NOMBRE_ESTABLECIMIENTO: empresa.NOMBRE_COMERCIAL,
        REPRESENTANTE_LEGAL: empresa.REPRESENTANTE,
        DIRECCION: empresa.DIRECCION,
        TELEFONO_EMPRESA: empresa.TELEFONO,
        EMAIL_EMPRESA: empresa.EMAIL,
        // Campos MSP
        CODIGO_INEC:          empresa.CODIGO_INEC,
        TIPO_ESTABLECIMIENTO: empresa.TIPO_ESTABLECIMIENTO,
        NIVEL_ATENCION:       empresa.NIVEL_ATENCION,
        PROVINCIA:            empresa.PROVINCIA,
        CANTON:               empresa.CANTON,
        PARROQUIA:            empresa.PARROQUIA,
        DIRECTOR_MEDICO:      empresa.DIRECTOR_MEDICO,
        CARGO_DIRECTOR:       empresa.CARGO_DIRECTOR,
        REGISTRO_MSP_DIR:     empresa.REGISTRO_MSP_DIR,
        HORAS_EDICION:        empresa.HORAS_EDICION || 72,
        POLITICA_PRIVACIDAD_HC: empresa.POLITICA_PRIVACIDAD_HC || 'RESTRINGIDA',
        COLOR_PRIMARIO:   colors['--navy']   || '#1a2535',
        COLOR_ACENTO:     colors['--accent'] || 'var(--navy-lt)',
        COLOR_ACENTO_LT:  colors['--bg']     || '#f0f4f8',
      });
    }catch(e){ console.error(e); }
    saveConfig(cfg); setConfig(cfg);
    showToast('✓ Configuración guardada');
  };

  /* Facturación electrónica SRI — identidad del Centro + ambiente */
  const guardarSriCentro = async () => {
    setSavingSri(true);
    try{
      await api.put('/configuracion/ms-config', {
        SRI_ESTAB: sriCentro.SRI_ESTAB || '001',
        SRI_PTO_EMI: sriCentro.SRI_PTO_EMI || '001',
        SRI_AMBIENTE: sriCentro.SRI_AMBIENTE || '1',
      });
      showToast('✓ Configuración SRI del Centro guardada');
    }catch(e){ showToast('✗ Error: '+(e.response?.data?.error||e.message)); }
    finally{ setSavingSri(false); }
  };

  const subirP12Centro = async file => {
    if(!file) return;
    if(!p12CentroPass.trim()){ showToast('Ingresa la contraseña del .p12 primero'); return; }
    setSubiendoP12Centro(true);
    const fd = new FormData();
    fd.append('p12', file); fd.append('p12_pass', p12CentroPass);
    try{
      await api.post('/configuracion/centro/p12', fd, {headers:{'Content-Type':'multipart/form-data'}});
      showToast('✓ Certificado .p12 del Centro guardado y verificado');
      setSriCentro(s=>({...s,TIENE_P12_CENTRO:true}));
      setP12CentroPass('');
    }catch(e){ showToast('✗ '+(e.response?.data?.error||e.message)); }
    finally{ setSubiendoP12Centro(false); }
  };

  const eliminarP12Centro = async () => {
    try{
      await api.delete('/configuracion/centro/p12');
      setSriCentro(s=>({...s,TIENE_P12_CENTRO:false}));
      showToast('Certificado del Centro eliminado');
    }catch(e){ showToast('✗ Error: '+(e.response?.data?.error||e.message)); }
  };

  /* Puntos de facturación por categoría */
  const setPuntoCampo = (cat, campo, valor) => {
    setPuntos(p=>({...p, [cat]: { ...(p[cat]||{}), [campo]: valor }}));
  };
  const quitarPunto = cat => {
    setPuntos(p=>{ const n={...p}; delete n[cat]; return n; });
  };
  const guardarPuntos = async () => {
    setSavingPuntos(true);
    try{
      const{data}=await api.put('/configuracion/puntos-facturacion', { puntos });
      setPuntos(data.puntos||{});
      showToast('✓ Puntos de facturación guardados');
    }catch(e){ showToast('✗ Error: '+(e.response?.data?.error||e.message)); }
    finally{ setSavingPuntos(false); }
  };

  const usuariosFiltrados = usuarios.filter(u => {
    const q = filtro.toLowerCase();
    const matchQ = !q || (u.NOMBRES||'').toLowerCase().includes(q) || (u.USUARIO||'').toLowerCase().includes(q);
    const matchT = !filtroTipo || u.TIPO_USUARIO === filtroTipo;
    return matchQ && matchT;
  });

  const TABS_ALL = [
    {id:'usuarios',        icon:'👥', label:'Usuarios y roles',       admin: false},
    {id:'general',         icon:'⚙',  label:'Configuración general',  admin: true},
    {id:'estilos',         icon:'🎨', label:'Estilos e identidad',    admin: true},
    {id:'facturacion',     icon:'🧾', label:'Facturación electrónica', admin: true},
    {id:'tarifario',       icon:'💲', label:'Tarifario',              admin: false},
    {id:'examenes',        icon:'🧪', label:'Exámenes y grupos',      admin: true},
    {id:'plantillas',      icon:'📋', label:'Plantillas de consulta', admin: false},
    {id:'consentimientos', icon:'✍️', label:'Consentimientos',        admin: false},
    {id:'ia',              icon:'🤖', label:'Asistente IA',           admin: true},
  ];
  const TABS = esAdmin ? TABS_ALL : TABS_ALL.filter(t => !t.admin);

  return (
    <div className="fade-up" style={{position:'relative'}}>
      {toast&&<div style={{position:'fixed',top:20,right:24,background:'var(--green)',color:'#fff',
        padding:'9px 18px',borderRadius:8,fontSize:12,fontWeight:600,zIndex:999,boxShadow:'0 4px 12px rgba(0,0,0,.2)'}}>
        {toast}</div>}

      {/* Tabs */}
      <div style={{display:'flex',gap:8,marginBottom:18,flexWrap:'wrap'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={`btn ${tab===t.id?'btn-primary':''}`} style={{gap:7}}>
            <span style={{fontSize:13}}>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ USUARIOS ═══════════════════════════════════════ */}
      {tab==='usuarios'&&(
        <div>
          {/* Header */}
          <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
            <input className="form-control" style={{maxWidth:260}} placeholder="Buscar usuario..."
              value={filtro} onChange={e=>setFiltro(e.target.value)}/>
            <select className="form-control" style={{maxWidth:180}} value={filtroTipo} onChange={e=>setFTipo(e.target.value)}>
              <option value="">Todos los tipos</option>
              {TIPOS_USUARIO.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div style={{marginLeft:'auto'}}>
              <button className="btn btn-primary" onClick={abrirNuevo}>+ Nuevo usuario</button>
            </div>
          </div>

          {/* Stats por tipo */}
          <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
            {TIPOS_USUARIO.map(t=>{
              const cnt = usuarios.filter(u=>u.TIPO_USUARIO===t.value).length;
              if(!cnt) return null;
              return(
                <div key={t.value} onClick={()=>setFTipo(filtroTipo===t.value?'':t.value)}
                  style={{background:filtroTipo===t.value?t.color:'var(--bg)',color:filtroTipo===t.value?'#fff':'var(--text2)',
                    border:`1px solid ${t.color}30`,borderRadius:20,padding:'4px 12px',fontSize:11,
                    cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontWeight:500,transition:'all .15s'}}>
                  <span>{t.icon}</span> {t.label} ({cnt})
                </div>
              );
            })}
          </div>

          {/* Tabla */}
          <div className="table-card">
            <div className="table-header">
              <span className="card-title">Usuarios del sistema ({usuariosFiltrados.length})</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Usuario</th><th>Nombre</th><th>Tipo</th>
                    <th>Especialidad / Registro</th><th>Email</th>
                    <th>Estado</th><th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosFiltrados.length===0
                    ? <tr><td colSpan={7} className="empty-state">Sin usuarios</td></tr>
                    : usuariosFiltrados.map(u=>{
                        const tipo = tipoInfo(u.TIPO_USUARIO);
                        return(
                          <tr key={u.USUARIO} style={{opacity:u.ACTIVO?1:.5}}>
                            <td style={{fontWeight:700,color:'var(--navy)'}}>{u.USUARIO}</td>
                            <td style={{fontWeight:500}}>{u.NOMBRES}</td>
                            <td>
                              <span style={{background:tipo.color+'18',color:tipo.color,
                                padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:500,
                                display:'inline-flex',alignItems:'center',gap:4}}>
                                {tipo.icon} {tipo.label}
                              </span>
                              {u.FACTURA_RUC_PROPIO&&(
                                <span title="Factura con su propio RUC" style={{marginLeft:5,fontSize:10,
                                  padding:'1px 6px',borderRadius:10,background:'#FFF3D6',color:'#8A6300',
                                  fontWeight:600}}>🧾 RUC propio</span>
                              )}
                            </td>
                            <td style={{fontSize:11,color:'var(--text2)'}}>
                              {u.ESPECIALIDAD&&<div style={{fontWeight:500}}>{u.ESPECIALIDAD}</div>}
                              {u.REGISTRO_MSP&&<div style={{color:'var(--text3)'}}>MSP: {u.REGISTRO_MSP}</div>}
                              {u.SENESCYT&&<div style={{color:'var(--text3)'}}>SENESCYT: {u.SENESCYT}</div>}
                              {!u.ESPECIALIDAD&&!u.REGISTRO_MSP&&'—'}
                            </td>
                            <td style={{color:'var(--text3)',fontSize:11}}>{u.EMAIL||'—'}</td>
                            <td>
                              <span className={`badge ${u.ACTIVO?'badge-green':''}`}
                                style={!u.ACTIVO?{background:'var(--border)',color:'var(--text3)'}:{}}>
                                {u.ACTIVO?'Activo':'Inactivo'}
                              </span>
                            </td>
                            <td>
                              <div style={{display:'flex',gap:4}}>
                                <button className="btn btn-sm" style={{padding:'3px 8px',fontSize:11}}
                                  onClick={()=>abrirEditar(u)}>✏️ Editar</button>
                                <button className="btn btn-sm" style={{padding:'3px 8px',fontSize:11,
                                  color:u.ACTIVO?'var(--red)':'var(--green)'}}
                                  onClick={()=>toggleActivo(u.USUARIO,u.ACTIVO)}>
                                  {u.ACTIVO?'Desactivar':'Activar'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ GENERAL ════════════════════════════════════════ */}
      {tab==='general'&&(
        <div className="card" style={{padding:0}}>
          <div className="card-header"><span className="card-title">Datos del establecimiento</span></div>
          <div style={{padding:'0 20px'}}>

            {/* ── Sección: Identificación ── */}
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',
              color:'var(--text3)',padding:'12px 0 4px'}}>Identificación del establecimiento</div>
            {[{key:'RUC',label:'RUC',req:true,half:true},
              {key:'CODIGO_INEC',label:'Código INEC / MSP',half:true},
              {key:'RAZON_SOCIAL',label:'Razón social'},
              {key:'NOMBRE_COMERCIAL',label:'Nombre comercial',req:true},
              {key:'REPRESENTANTE',label:'Representante legal',req:true},
            ].map(f=>(
              <div key={f.key} style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,fontSize:12,color:'var(--text2)',fontWeight:500,flexShrink:0}}>
                  {f.label}{f.req&&<span style={{color:'var(--red)'}}> *</span>}
                </div>
                <input className="form-control" style={{maxWidth:f.half?240:'100%'}}
                  value={empresa[f.key]||''}
                  onChange={e=>setEmpresa(ep=>({...ep,[f.key]:e.target.value}))}/>
              </div>
            ))}

            {/* Tipo y nivel — selects */}
            {[
              {key:'TIPO_ESTABLECIMIENTO', label:'Tipo de establecimiento', opts:[
                'Consultorio Médico','Centro de Salud Tipo A','Centro de Salud Tipo B',
                'Centro de Salud Tipo C','Hospital Básico','Hospital General','Clínica']},
              {key:'NIVEL_ATENCION', label:'Nivel de atención', opts:[
                'Primer Nivel','Segundo Nivel','Tercer Nivel']},
            ].map(f=>(
              <div key={f.key} style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,fontSize:12,color:'var(--text2)',fontWeight:500,flexShrink:0}}>{f.label}</div>
                <select className="form-control" style={{maxWidth:320}}
                  value={empresa[f.key]||''}
                  onChange={e=>setEmpresa(ep=>({...ep,[f.key]:e.target.value}))}>
                  {f.opts.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}

            {/* ── Sección: Ubicación ── */}
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',
              color:'var(--text3)',padding:'14px 0 4px'}}>Ubicación</div>
            {[
              {key:'PROVINCIA',label:'Provincia',half:true},
              {key:'CANTON',label:'Cantón',half:true},
              {key:'PARROQUIA',label:'Parroquia',half:true},
              {key:'DIRECCION',label:'Dirección completa'},
              {key:'TELEFONO',label:'Teléfono',half:true},
              {key:'EMAIL',label:'Email institucional',half:true},
            ].map(f=>(
              <div key={f.key} style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,fontSize:12,color:'var(--text2)',fontWeight:500,flexShrink:0}}>{f.label}</div>
                <input className="form-control" style={{maxWidth:f.half?240:'100%'}}
                  value={empresa[f.key]||''}
                  onChange={e=>setEmpresa(ep=>({...ep,[f.key]:e.target.value}))}/>
              </div>
            ))}

            {/* ── Sección: Director médico (para firma en documentos MSP) ── */}
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',
              color:'var(--text3)',padding:'14px 0 4px'}}>Director/a médico/a — firma en documentos MSP</div>
            {[
              {key:'DIRECTOR_MEDICO',label:'Nombre completo',req:true},
              {key:'CARGO_DIRECTOR',label:'Cargo',half:true},
              {key:'REGISTRO_MSP_DIR',label:'Registro MSP',half:true},
            ].map(f=>(
              <div key={f.key} style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,fontSize:12,color:'var(--text2)',fontWeight:500,flexShrink:0}}>
                  {f.label}{f.req&&<span style={{color:'var(--red)'}}> *</span>}
                </div>
                <input className="form-control" style={{maxWidth:f.half?240:'100%'}}
                  value={empresa[f.key]||''}
                  onChange={e=>setEmpresa(ep=>({...ep,[f.key]:e.target.value}))}/>
              </div>
            ))}

            {/* ── Sección: Control de historia clínica ── */}
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',
              color:'var(--text3)',padding:'14px 0 4px'}}>Control de historia clínica</div>
            <div style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
              <div style={{width:220,flexShrink:0}}>
                <div style={{fontSize:12,color:'var(--text2)',fontWeight:500}}>Horas para edición libre</div>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>
                  El médico puede editar sin autorización dentro de este plazo desde la creación
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <input type="number" className="form-control" style={{width:90,textAlign:'right',fontWeight:600}}
                  min={1} max={168} value={empresa.HORAS_EDICION||72}
                  onChange={e=>setEmpresa(ep=>({...ep,HORAS_EDICION:parseInt(e.target.value)||72}))}/>
                <span style={{fontSize:12,color:'var(--text2)'}}>horas</span>
                <span style={{fontSize:11,color:'var(--text3)'}}>
                  ({Math.floor((empresa.HORAS_EDICION||72)/24)}d {(empresa.HORAS_EDICION||72)%24}h)
                </span>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
              <div style={{width:220,flexShrink:0}}>
                <div style={{fontSize:12,color:'var(--text2)',fontWeight:500}}>Privacidad del historial clínico</div>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>
                  Quién puede ver las consultas registradas por otro médico
                </div>
              </div>
              <select className="form-control" style={{maxWidth:280}}
                value={empresa.POLITICA_PRIVACIDAD_HC||'RESTRINGIDA'}
                onChange={e=>setEmpresa(ep=>({...ep,POLITICA_PRIVACIDAD_HC:e.target.value}))}>
                <option value="RESTRINGIDA">Restringida — solo con interconsulta</option>
                <option value="ABIERTA">Abierta — cualquier médico ve todo</option>
              </select>
            </div>
            <div style={{padding:'12px 0 6px',fontSize:11,fontWeight:700,textTransform:'uppercase',
              letterSpacing:'.5px',color:'var(--text3)'}}>Opciones del sistema</div>
            {CHECKS_CONFIG.map(c=>(
              <div key={c.key} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 0',
                borderBottom:'1px solid var(--bg)',cursor:'pointer'}}
                onClick={()=>setChecks(ch=>({...ch,[c.key]:!ch[c.key]}))}>
                <div style={{width:20,height:20,borderRadius:5,flexShrink:0,display:'flex',alignItems:'center',
                  justifyContent:'center',background:checks[c.key]?'var(--navy)':'var(--surface)',
                  border:`2px solid ${checks[c.key]?'var(--navy)':'var(--border)'}`,transition:'all .15s'}}>
                  {checks[c.key]&&<span style={{color:'#fff',fontSize:11,fontWeight:700}}>✓</span>}
                </div>
                <span style={{fontSize:12,color:'var(--text2)'}}>{c.label}</span>
              </div>
            ))}
          </div>
          <div style={{padding:'16px 20px',display:'flex',gap:10}}>
            <button className="btn btn-primary" onClick={guardarGeneral}>Guardar cambios</button>
          </div>
        </div>
      )}

      {/* ═══ ESTILOS ════════════════════════════════════════ */}
      {tab==='estilos'&&(
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="card">
            <div className="card-header"><span className="card-title">Logo del establecimiento</span></div>
            <div style={{padding:'0 20px 20px'}}>
              <div onClick={()=>logoRef.current.click()}
                style={{border:'2px dashed var(--border)',borderRadius:10,padding:24,textAlign:'center',
                  background:'var(--surface2)',cursor:'pointer',marginBottom:12}}>
                {logo?<img src={logo} alt="Logo" style={{maxHeight:60,maxWidth:220,objectFit:'contain'}}/>
                  :<><div style={{fontSize:28,opacity:.3,marginBottom:6}}>🏥</div>
                    <div style={{fontSize:12,color:'var(--text3)'}}>Clic para subir el logo</div></>}
              </div>
              <input ref={logoRef} type="file" accept="image/*" style={{display:'none'}} onChange={async e=>{
                const f=e.target.files[0]; if(!f) return;
                const fd=new FormData(); fd.append('logo',f);
                try{
                  const {data}=await api.post('/configuracion/logo',fd,{headers:{'Content-Type':'multipart/form-data'}});
                  const base=(process.env.REACT_APP_API_URL||'').replace('/api','').replace(/\/$/,'');
                  setLogo(`${base}${data.LOGO_PATH}?t=${Date.now()}`);
                  showToast('✓ Logo guardado');
                }catch(err){ showToast('Error al subir el logo: '+(err.response?.data?.error||err.message)); }
              }}/>
              {logo&&<button className="btn" style={{fontSize:11,color:'var(--red)'}} onClick={async()=>{
                try{ await api.delete('/configuracion/logo'); setLogo(null); showToast('Logo eliminado'); }
                catch(err){ showToast('Error: '+(err.response?.data?.error||err.message)); }
              }}>Eliminar</button>}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Colores institucionales</span></div>
            <div style={{padding:'0 20px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
                {COLORS_CONFIG.map(c=>(
                  <div key={c.key} style={{display:'flex',flexDirection:'column',gap:5}}>
                    <div style={{fontSize:11,color:'var(--text2)',fontWeight:600}}>{c.label}</div>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <div style={{width:32,height:26,borderRadius:5,background:colors[c.key],
                        border:'1px solid var(--border)',cursor:'pointer',position:'relative'}}
                        onClick={()=>document.getElementById('cp-'+c.key.replace('--','')).click()}>
                        <input id={'cp-'+c.key.replace('--','')} type="color" value={colors[c.key]}
                          style={{opacity:0,width:0,height:0,position:'absolute'}}
                          onInput={e=>setColors(cl=>({...cl,[c.key]:e.target.value}))}/>
                      </div>
                      <input style={{flex:1,padding:'4px 6px',border:'1px solid var(--border)',borderRadius:5,
                        fontSize:10,fontFamily:'monospace',background:'var(--surface2)',outline:'none'}}
                        value={colors[c.key]} maxLength={7}
                        onChange={e=>/^#[0-9a-fA-F]{6}$/.test(e.target.value)&&setColors(cl=>({...cl,[c.key]:e.target.value}))}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{padding:'14px 20px',display:'flex',gap:10}}>
              <button className="btn btn-primary" onClick={guardarGeneral}>Guardar colores</button>
              <button className="btn" onClick={()=>setColors(Object.fromEntries(COLORS_CONFIG.map(c=>[c.key,c.default])))}>
                Restaurar por defecto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ FACTURACIÓN ELECTRÓNICA (SRI) ══════════════════ */}
      {tab==='facturacion'&&(
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Identidad SRI del Centro */}
          <div className="card" style={{padding:0}}>
            <div className="card-header"><span className="card-title">Identidad SRI del Centro</span></div>
            <div style={{padding:'0 20px'}}>
              <div style={{padding:'10px 0',fontSize:11,color:'var(--text3)'}}>
                El RUC, razón social, dirección y teléfono del Centro se editan en
                {' '}<b>Configuración general</b>. Acá solo va lo específico de facturación electrónica.
              </div>
              <div style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,fontSize:12,color:'var(--text2)',fontWeight:500,flexShrink:0}}>Establecimiento (estab)</div>
                <input className="form-control" style={{maxWidth:120}} placeholder="001"
                  value={sriCentro.SRI_ESTAB}
                  onChange={e=>setSriCentro(s=>({...s,SRI_ESTAB:e.target.value}))}/>
              </div>
              <div style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,fontSize:12,color:'var(--text2)',fontWeight:500,flexShrink:0}}>Punto de emisión</div>
                <input className="form-control" style={{maxWidth:120}} placeholder="001"
                  value={sriCentro.SRI_PTO_EMI}
                  onChange={e=>setSriCentro(s=>({...s,SRI_PTO_EMI:e.target.value}))}/>
              </div>
              <div style={{display:'flex',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--bg)'}}>
                <div style={{width:220,flexShrink:0}}>
                  <div style={{fontSize:12,color:'var(--text2)',fontWeight:500}}>Ambiente SRI</div>
                  <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>
                    Pruebas = comprobantes de prueba, no válidos tributariamente
                  </div>
                </div>
                <div style={{display:'flex',gap:14}}>
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                    <input type="radio" name="ambiente" checked={sriCentro.SRI_AMBIENTE==='2'}
                      onChange={()=>setSriCentro(s=>({...s,SRI_AMBIENTE:'2'}))}/>
                    Pruebas
                  </label>
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',
                    color:sriCentro.SRI_AMBIENTE==='1'?'var(--red)':'inherit',fontWeight:sriCentro.SRI_AMBIENTE==='1'?600:400}}>
                    <input type="radio" name="ambiente" checked={sriCentro.SRI_AMBIENTE==='1'}
                      onChange={()=>setSriCentro(s=>({...s,SRI_AMBIENTE:'1'}))}/>
                    Producción
                  </label>
                </div>
              </div>

              <div style={{padding:'14px 0 6px'}}>
                <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px',
                  color:'var(--text3)',marginBottom:8}}>Certificado de firma electrónica (.p12) del Centro</div>
                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
                  {sriCentro.TIENE_P12_CENTRO
                    ? <div style={{padding:'4px 10px',background:'var(--green-lt)',color:'var(--green)',
                        borderRadius:6,fontSize:11,fontWeight:500}}>✅ Certificado activo</div>
                    : <div style={{padding:'4px 10px',background:'var(--amber-lt,#FEF3E2)',color:'var(--amber)',
                        borderRadius:6,fontSize:11,fontWeight:500}}>⚠️ Sin certificado configurado</div>}
                  {sriCentro.TIENE_P12_CENTRO&&(
                    <button className="btn btn-sm" style={{fontSize:11,color:'var(--red)'}} onClick={eliminarP12Centro}>Eliminar</button>
                  )}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,maxWidth:480}}>
                  <div>
                    <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>Contraseña del .p12</label>
                    <input className="form-control" type="password" placeholder="Contraseña del certificado"
                      value={p12CentroPass} onChange={e=>setP12CentroPass(e.target.value)}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>Archivo .p12</label>
                    <label style={{cursor:'pointer',display:'block'}}>
                      <span className="btn btn-sm" style={{fontSize:11,width:'100%',display:'block',textAlign:'center'}}>
                        {subiendoP12Centro?'Verificando...':sriCentro.TIENE_P12_CENTRO?'🔄 Actualizar .p12':'📁 Seleccionar .p12'}
                      </span>
                      <input ref={p12CentroRef} type="file" accept=".p12" style={{display:'none'}}
                        onChange={e=>{ const f=e.target.files[0]; if(f) subirP12Centro(f); }}/>
                    </label>
                  </div>
                </div>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:5}}>
                  Se usa para firmar facturas que van al RUC del Centro (cuando el médico asignado no tiene RUC propio).
                </div>
              </div>
            </div>
            <div style={{padding:'16px 20px',display:'flex',gap:10}}>
              <button className="btn btn-primary" disabled={savingSri} onClick={guardarSriCentro}>
                {savingSri?'Guardando...':'Guardar cambios'}
              </button>
            </div>
          </div>

          {/* Puntos de facturación por categoría */}
          <div className="card" style={{padding:0}}>
            <div className="card-header"><span className="card-title">Puntos de facturación por categoría</span></div>
            <div style={{padding:'0 20px'}}>
              <div style={{padding:'10px 0',fontSize:11,color:'var(--text3)'}}>
                Solo necesario si tu clínica factura Farmacia, Laboratorio, etc. con un punto de emisión
                (o dirección/teléfono) distinto al de Turnos/Consulta. Si tu clínica factura todo con un solo
                punto de emisión, no configures nada acá.
              </div>
              {categoriasFact.map(cat=>{
                const activo = !!puntos[cat.value];
                const cfg = puntos[cat.value]||{};
                return (
                  <div key={cat.value} style={{padding:'12px 0',borderBottom:'1px solid var(--bg)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}
                      onClick={()=>{
                        if(activo) quitarPunto(cat.value);
                        else setPuntos(p=>({...p,[cat.value]:{pto_emi:'',estab:'',direccion:'',telefono:''}}));
                      }}>
                      <div style={{width:20,height:20,borderRadius:5,flexShrink:0,display:'flex',alignItems:'center',
                        justifyContent:'center',background:activo?'var(--navy)':'var(--surface)',
                        border:`2px solid ${activo?'var(--navy)':'var(--border)'}`,transition:'all .15s'}}>
                        {activo&&<span style={{color:'#fff',fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:12,fontWeight:500}}>{cat.label}</span>
                      <span style={{fontSize:10,color:'var(--text3)'}}>({cat.value})</span>
                    </div>
                    {activo&&(
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:8,marginTop:10,marginLeft:30}}>
                        <div>
                          <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>Punto de emisión</label>
                          <input className="form-control" style={{fontSize:12}} placeholder="Ej: 500"
                            value={cfg.pto_emi||''} onChange={e=>setPuntoCampo(cat.value,'pto_emi',e.target.value)}/>
                        </div>
                        <div>
                          <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>Establecimiento</label>
                          <input className="form-control" style={{fontSize:12}} placeholder="Igual al base si vacío"
                            value={cfg.estab||''} onChange={e=>setPuntoCampo(cat.value,'estab',e.target.value)}/>
                        </div>
                        <div>
                          <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>Dirección</label>
                          <input className="form-control" style={{fontSize:12}} placeholder="Igual al base si vacío"
                            value={cfg.direccion||''} onChange={e=>setPuntoCampo(cat.value,'direccion',e.target.value)}/>
                        </div>
                        <div>
                          <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>Teléfono</label>
                          <input className="form-control" style={{fontSize:12}} placeholder="Igual al base si vacío"
                            value={cfg.telefono||''} onChange={e=>setPuntoCampo(cat.value,'telefono',e.target.value)}/>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{padding:'16px 20px',display:'flex',gap:10}}>
              <button className="btn btn-primary" disabled={savingPuntos} onClick={guardarPuntos}>
                {savingPuntos?'Guardando...':'Guardar puntos de facturación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL USUARIO ══════════════════════════════════ */}
      {showModal&&createPortal(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:9999,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div style={{background:'var(--bg)',borderRadius:14,width:640,
            maxHeight:'85vh',display:'flex',flexDirection:'column',
            border:'0.5px solid var(--border)',margin:'0 16px'}}>
            {/* Header fijo */}
            <div style={{padding:'14px 20px',borderBottom:'0.5px solid var(--border)',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontSize:14,fontWeight:500}}>
                {editUser ? `Editar usuario — ${editUser}` : 'Nuevo usuario'}
              </span>
              <button className="btn btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>

            <div style={{padding:20,display:'flex',flexDirection:'column',gap:14,overflowY:'auto',flex:1}}>
              {errMsg&&<div style={{padding:'8px 12px',borderRadius:8,background:'#FCEBEB',color:'#A32D2D',fontSize:12}}>{errMsg}</div>}

              {/* Tipo de usuario */}
              <div>
                <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px',
                  color:'var(--text3)',marginBottom:8}}>Tipo de usuario</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {TIPOS_USUARIO.map(t=>(
                    <div key={t.value} onClick={()=>setForm(f=>({...f,TIPO_USUARIO:t.value}))}
                      style={{padding:'7px 14px',borderRadius:8,cursor:'pointer',fontSize:12,
                        display:'flex',alignItems:'center',gap:5,transition:'all .15s',fontWeight:500,
                        background:form.TIPO_USUARIO===t.value?t.color:'var(--surface)',
                        color:form.TIPO_USUARIO===t.value?'#fff':'var(--text2)',
                        border:`1px solid ${form.TIPO_USUARIO===t.value?t.color:'var(--border)'}`}}>
                      {t.icon} {t.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Datos básicos */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                {!editUser&&(
                  <div className="form-group">
                    <label className="form-label">Usuario (login) *</label>
                    <input className="form-control" placeholder="Ej: JPEREZ" style={{textTransform:'uppercase'}}
                      value={form.USUARIO} onChange={e=>setForm(f=>({...f,USUARIO:e.target.value.toUpperCase()}))}/>
                  </div>
                )}
                <div className="form-group" style={!editUser?{}:{gridColumn:'1/-1'}}>
                  <label className="form-label">Nombre completo *</label>
                  <input className="form-control" placeholder="DR. JUAN PÉREZ"
                    value={form.NOMBRES} onChange={e=>setForm(f=>({...f,NOMBRES:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Cédula del profesional</label>
                  <input className="form-control" placeholder="1234567890"
                    value={form.CEDULA||''} onChange={e=>setForm(f=>({...f,CEDULA:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">{editUser?'Nueva contraseña (dejar vacío para no cambiar)':'Contraseña *'}</label>
                  <input className="form-control" type="password" placeholder="••••••••"
                    value={form.PWD} onChange={e=>setForm(f=>({...f,PWD:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-control" type="email" placeholder="doctor@clinica.com"
                    value={form.EMAIL} onChange={e=>setForm(f=>({...f,EMAIL:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" placeholder="09XXXXXXXX"
                    value={form.TELEFONO} onChange={e=>setForm(f=>({...f,TELEFONO:e.target.value}))}/>
                </div>
              </div>

              {/* Datos médicos (solo para MEDICO y ENFERMERIA) */}
              {['MEDICO','ENFERMERIA'].includes(form.TIPO_USUARIO)&&(
                <div style={{background:'var(--surface)',borderRadius:8,padding:14,
                  border:'0.5px solid var(--border)'}}>
                  <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px',
                    color:'var(--navy-lt)',marginBottom:10}}>🩺 Datos profesionales</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div className="form-group" style={{gridColumn:'1/-1'}}>
                      <label className="form-label">Especialidad</label>
                      <input className="form-control" placeholder="Ej: Medicina General, Endocrinología..."
                        value={form.ESPECIALIDAD} onChange={e=>setForm(f=>({...f,ESPECIALIDAD:e.target.value}))}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Registro MSP</label>
                      <input className="form-control" placeholder="N° registro Ministerio"
                        value={form.REGISTRO_MSP} onChange={e=>setForm(f=>({...f,REGISTRO_MSP:e.target.value}))}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Registro SENESCYT</label>
                      <input className="form-control" placeholder="N° registro SENESCYT"
                        value={form.SENESCYT} onChange={e=>setForm(f=>({...f,SENESCYT:e.target.value}))}/>
                    </div>
                  </div>
                </div>
              )}
              {/* ── Sección Facturación electrónica SRI (solo médicos) ──
                   Activa/desactiva que este médico facture con su propio
                   RUC en vez del RUC del Centro — sin tocar código, para
                   cualquier clínica. Ver facturaVaAlCentro() en facturacion.js. */}
              {form.TIPO_USUARIO==='MEDICO'&&editUser&&(
                <div style={{background:'#FFF9EC',borderRadius:8,padding:14,
                  border:'0.5px solid #E8D6A0'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',marginBottom:facturaRucPropio?12:0}}
                    onClick={()=>setFacturaRucPropio(v=>!v)}>
                    <div style={{width:20,height:20,borderRadius:5,flexShrink:0,display:'flex',alignItems:'center',
                      justifyContent:'center',background:facturaRucPropio?'var(--navy)':'var(--surface)',
                      border:`2px solid ${facturaRucPropio?'var(--navy)':'var(--border)'}`,transition:'all .15s'}}>
                      {facturaRucPropio&&<span style={{color:'#fff',fontSize:11,fontWeight:700}}>✓</span>}
                    </div>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px',
                        color:'var(--navy-lt)'}}>🧾 Factura con su propio RUC</div>
                      <div style={{fontSize:10,color:'var(--text3)',marginTop:1}}>
                        Si está apagado, sus facturas se emiten con el RUC del Centro (comportamiento normal)
                      </div>
                    </div>
                  </div>
                  {facturaRucPropio&&(
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                      <div className="form-group" style={{gridColumn:'1/-1'}}>
                        <label className="form-label">RUC propio</label>
                        <input className="form-control" placeholder="13 dígitos"
                          value={form.SRI_RUC} onChange={e=>setForm(f=>({...f,SRI_RUC:e.target.value}))}/>
                      </div>
                      <div className="form-group" style={{gridColumn:'1/-1'}}>
                        <label className="form-label">Razón social para la factura</label>
                        <input className="form-control" placeholder="Ej: DR. JUAN PÉREZ (o su nombre comercial)"
                          value={form.SRI_RAZON_SOCIAL} onChange={e=>setForm(f=>({...f,SRI_RAZON_SOCIAL:e.target.value}))}/>
                      </div>
                      <div className="form-group" style={{gridColumn:'1/-1'}}>
                        <label className="form-label">Dirección para la factura</label>
                        <input className="form-control" placeholder="Si está vacío, se usa el teléfono/dirección del usuario"
                          value={form.SRI_DIRECCION} onChange={e=>setForm(f=>({...f,SRI_DIRECCION:e.target.value}))}/>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Establecimiento (estab)</label>
                        <input className="form-control" placeholder="001"
                          value={form.SRI_ESTAB} onChange={e=>setForm(f=>({...f,SRI_ESTAB:e.target.value}))}/>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Punto de emisión</label>
                        <input className="form-control" placeholder="001"
                          value={form.SRI_PTO_EMI} onChange={e=>setForm(f=>({...f,SRI_PTO_EMI:e.target.value}))}/>
                      </div>
                      <div style={{gridColumn:'1/-1',fontSize:10,color:'var(--text3)'}}>
                        Necesita también su certificado .p12 cargado abajo, para poder firmar sus propias facturas.
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* ── Sección Firmas (solo médicos) ── */}
              {['MEDICO','ENFERMERIA'].includes(form.TIPO_USUARIO)&&editUser&&(
                <div style={{background:'#F8F6FF',borderRadius:8,padding:14,
                  border:'0.5px solid #AFA9EC'}}>
                  <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',
                    letterSpacing:'.4px',color:'var(--navy-lt)',marginBottom:12}}>
                    🔐 Firma electrónica y visual
                  </div>

                  {/* Firma como texto */}
                  <div className="form-group" style={{marginBottom:10}}>
                    <label className="form-label">Firma como texto</label>
                    <div style={{display:'flex',gap:6}}>
                      <input className="form-control" placeholder="Ej: Dr. José Caicedo — Médico General"
                        value={firmaTexto} onChange={e=>setFirmaTxt(e.target.value)}/>
                      <button type="button" className="btn btn-sm btn-primary"
                        disabled={subiendo==='txt'}
                        onClick={async()=>{
                          setSubiendo('txt');
                          try{
                            await api.post(`/configuracion/usuarios/${editUser}/firma-texto`,{FIRMA_TEXTO:firmaTexto});
                            showToast('✓ Firma de texto guardada');
                          }catch(e){setErrMsg(e.response?.data?.error||e.message);}
                          setSubiendo('');
                        }}>
                        {subiendo==='txt'?'...':'Guardar'}
                      </button>
                    </div>
                  </div>

                  {/* Firma imagen */}
                  <div className="form-group" style={{marginBottom:10}}>
                    <label className="form-label">Firma manuscrita (imagen)</label>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      {firmaInfo.tiene_firma_img&&(
                        <div style={{padding:'4px 10px',background:'var(--green-lt)',color:'var(--green)',
                          borderRadius:6,fontSize:11,fontWeight:500}}>✅ Imagen cargada</div>
                      )}
                      <label style={{cursor:'pointer'}}>
                        <span className="btn btn-sm" style={{fontSize:11}}>
                          {firmaInfo.tiene_firma_img?'🔄 Cambiar imagen':'📷 Subir imagen'}
                        </span>
                        <input type="file" accept="image/*" style={{display:'none'}}
                          onChange={async e=>{
                            const file=e.target.files[0]; if(!file) return;
                            setSubiendo('img');
                            const fd=new FormData(); fd.append('firma',file);
                            try{
                              await api.post(`/configuracion/usuarios/${editUser}/firma-imagen`,fd,
                                {headers:{'Content-Type':'multipart/form-data'}});
                              showToast('✓ Imagen de firma guardada');
                              setFirmaInfo(fi=>({...fi,tiene_firma_img:true}));
                            }catch(e){setErrMsg(e.response?.data?.error||e.message);}
                            setSubiendo('');
                          }}/>
                      </label>
                    </div>
                  </div>

                  {/* Certificado .p12 */}
                  <div className="form-group">
                    <label className="form-label">Certificado de firma electrónica (.p12)</label>
                    <div style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
                      {firmaInfo.tiene_p12&&(
                        <div style={{padding:'4px 10px',background:'var(--green-lt)',color:'var(--green)',
                          borderRadius:6,fontSize:11,fontWeight:500}}>✅ Certificado activo</div>
                      )}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                      <div>
                        <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>
                          Contraseña del .p12
                        </label>
                        <input className="form-control" type="password" placeholder="Contraseña del certificado"
                          value={p12Pass} onChange={e=>setP12Pass(e.target.value)}/>
                      </div>
                      <div>
                        <label style={{fontSize:10,color:'var(--text3)',display:'block',marginBottom:3}}>
                          Archivo .p12
                        </label>
                        <label style={{cursor:'pointer',display:'block'}}>
                          <span className="btn btn-sm" style={{fontSize:11,width:'100%',display:'block',textAlign:'center'}}>
                            {subiendo==='p12'?'Verificando...': firmaInfo.tiene_p12?'🔄 Actualizar .p12':'📁 Seleccionar .p12'}
                          </span>
                          <input type="file" accept=".p12" style={{display:'none'}}
                            onChange={async e=>{
                              const file=e.target.files[0]; if(!file) return;
                              if(!p12Pass.trim()){setErrMsg('Ingresa la contraseña del .p12 primero');return;}
                              setSubiendo('p12');
                              const fd=new FormData();
                              fd.append('p12',file);
                              fd.append('p12_pass',p12Pass);
                              try{
                                await api.post(`/configuracion/usuarios/${editUser}/p12`,fd,
                                  {headers:{'Content-Type':'multipart/form-data'}});
                                showToast('✓ Certificado .p12 guardado y verificado');
                                setFirmaInfo(fi=>({...fi,tiene_p12:true}));
                                setP12Pass('');
                              }catch(e){setErrMsg(e.response?.data?.error||e.message);}
                              setSubiendo('');
                            }}/>
                        </label>
                      </div>
                    </div>
                    <div style={{fontSize:10,color:'var(--text3)',marginTop:5}}>
                      ⚠️ La contraseña se almacena cifrada. El .p12 se guarda en el servidor de forma segura.
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{padding:'13px 20px',borderTop:'0.5px solid var(--border)',
              display:'flex',justifyContent:'flex-end',gap:8,background:'var(--bg)',flexShrink:0}}>
              <button className="btn" onClick={()=>setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={saving} onClick={guardarUsuario}>
                {saving?'Guardando...':editUser?'Actualizar usuario':'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
      {/* ═══ TARIFARIO ══════════════════════════════════════ */}
      {tab==='tarifario'&&(()=>{
        const filtrados = tarifario.filter(s=>{
          const q=filtroServ.toLowerCase();
          const matchQ=!q||(s.SERVICIO||'').toLowerCase().includes(q);
          const matchT=!filtroTipoServ||(filtroTipoServ==='__SIN_TIPO__'?!s.TIPO_SERVICIO:s.TIPO_SERVICIO===filtroTipoServ);
          return matchQ&&matchT;
        });
        // Conteo por tipo: parte del catálogo real de tipos_servicio (para ver también
        // categorías con 0 servicios) más cualquier TIPO_SERVICIO ya en uso que no
        // esté en el catálogo cargado, y un bucket aparte para servicios sin tipo.
        const nombresCatalogo = tiposServicio.map(t=>t.TIPO_SERVICIO);
        const nombresExtra = [...new Set(tarifario.map(s=>s.TIPO_SERVICIO).filter(t=>t&&!nombresCatalogo.includes(t)))];
        const sinTipoCount = tarifario.filter(s=>!s.TIPO_SERVICIO).length;
        const pills = [
          { key:'', label:'Todos', count: tarifario.length },
          ...[...nombresCatalogo, ...nombresExtra].sort().map(t=>({ key:t, label:t, count: tarifario.filter(s=>s.TIPO_SERVICIO===t).length })),
          ...(sinTipoCount>0 ? [{ key:'__SIN_TIPO__', label:'Sin tipo', count: sinTipoCount }] : []),
        ];
        return (
        <div>
          {/* IVA Global */}
          <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,
            padding:16,marginBottom:14,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px',
                color:'var(--text3)',marginBottom:4}}>IVA vigente (%)</div>
              <div style={{fontSize:12,color:'var(--text2)',maxWidth:320}}>
                Este % se aplica a todos los servicios que tengan marcado "Aplica IVA"
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginLeft:'auto'}}>
              <input type="number" className="form-control" style={{width:100,textAlign:'right',fontWeight:500,fontSize:18}}
                min="0" max="100" step="0.5"
                value={ivaGlobal}
                onChange={e=>setIvaGlobal(parseFloat(e.target.value)||0)}/>
              <span style={{fontSize:16,fontWeight:500}}>%</span>
            </div>
          </div>

          {/* Tabla servicios */}
          <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'11px 16px',borderBottom:'0.5px solid var(--border)',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
              <span style={{fontSize:13,fontWeight:500}}>
                Servicios y precios
                <span style={{fontSize:11,fontWeight:400,color:'var(--text3)',marginLeft:6}}>
                  ({(filtroServ||filtroTipoServ) ? `${filtrados.length} de ${tarifario.length}` : `${tarifario.length} en total`})
                </span>
              </span>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-sm" onClick={()=>{setNuevoServ({SERVICIO:'',VALOR:0,APLICA_IVA:false,VALOR_MODIFICABLE:true,COD_TIPO_SERVICIO:tiposServicio[0]?.COD||''});setShowNuevoServ(true);}}>
                  + Nuevo servicio
                </button>
                <button className="btn btn-primary btn-sm"
                disabled={savingTar}
                onClick={async()=>{
                  setSavingTar(true);
                  try{
                    await api.put('/configuracion/tarifario',{servicios:tarifario,iva_global:ivaGlobal});
                    showToast('✓ Tarifario guardado correctamente');
                  }catch(e){alert(e.response?.data?.error||e.message);}
                  setSavingTar(false);
                }}>
                {savingTar?'Guardando...':'💾 Guardar cambios'}
              </button>
              </div>
            </div>

            {/* Búsqueda + categorías con conteo. Las categorías salen del catálogo
                real de tipos_servicio, así que una categoría con 0 servicios también
                se ve (antes solo aparecían los tipos que ya tenían al menos un servicio
                creado, lo que hacía parecer que faltaban tipos). */}
            <div style={{padding:'10px 16px 12px',borderBottom:'0.5px solid var(--border)'}}>
              <input className="form-control" style={{maxWidth:280,marginBottom:10}} placeholder="Buscar servicio..."
                value={filtroServ} onChange={e=>setFiltroServ(e.target.value)}/>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {pills.map(p=>(
                  <button key={p.key||'__ALL__'} onClick={()=>setFiltroTipoServ(p.key)}
                    style={{padding:'5px 12px',borderRadius:20,fontSize:11,fontWeight:500,cursor:'pointer',
                      border:`1px solid ${filtroTipoServ===p.key?'var(--navy)':'var(--border)'}`,
                      background:filtroTipoServ===p.key?'var(--navy)':'var(--surface)',
                      color:filtroTipoServ===p.key?'#fff':p.count===0?'var(--text3)':'var(--text2)'}}>
                    {p.label} <span style={{opacity:.75}}>· {p.count}</span>
                  </button>
                ))}
                {(filtroServ||filtroTipoServ)&&(
                  <button className="btn btn-sm" onClick={()=>{setFiltroServ('');setFiltroTipoServ('');}}>Limpiar filtro</button>
                )}
              </div>
            </div>

            {showNuevoServ&&(
              <div style={{padding:14,background:'var(--surface)',borderBottom:'0.5px solid var(--border)'}}>
                <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:8,marginBottom:8}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Nombre del servicio</label>
                    <input className="form-control" style={{fontSize:12}} placeholder="Ej: RADIOGRAFIA DE TORAX"
                      value={nuevoServ.SERVICIO} onChange={e=>setNuevoServ(s=>({...s,SERVICIO:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Precio ($)</label>
                    <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12}}
                      value={nuevoServ.VALOR} onChange={e=>setNuevoServ(s=>({...s,VALOR:parseFloat(e.target.value)||0}))}/>
                  </div>
                </div>
                <div style={{marginBottom:10}}>
                  <label className="form-label" style={{fontSize:10}}>Tipo de servicio *</label>
                  <select className="form-control" style={{fontSize:12}} value={nuevoServ.COD_TIPO_SERVICIO}
                    onChange={e=>setNuevoServ(s=>({...s,COD_TIPO_SERVICIO:e.target.value}))}>
                    <option value="">— Selecciona un tipo —</option>
                    {tiposServicio.map(t=><option key={t.COD} value={t.COD}>{t.TIPO_SERVICIO}</option>)}
                  </select>
                  {tiposServicio.length===0&&(
                    <div style={{fontSize:10,color:'var(--amber)',marginTop:4}}>
                      No hay tipos de servicio en el catálogo — se deben crear primero en la base (tabla tipos_servicio).
                    </div>
                  )}
                </div>
                <div style={{display:'flex',gap:16,marginBottom:10}}>
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                    <input type="checkbox" checked={nuevoServ.APLICA_IVA}
                      onChange={e=>setNuevoServ(s=>({...s,APLICA_IVA:e.target.checked}))} style={{width:14,height:14,cursor:'pointer'}}/>
                    Aplica IVA
                  </label>
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                    <input type="checkbox" checked={nuevoServ.VALOR_MODIFICABLE}
                      onChange={e=>setNuevoServ(s=>({...s,VALOR_MODIFICABLE:e.target.checked}))} style={{width:14,height:14,cursor:'pointer'}}/>
                    Precio modificable
                  </label>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-primary btn-sm" disabled={savingServ||!nuevoServ.SERVICIO.trim()||!nuevoServ.COD_TIPO_SERVICIO} onClick={async()=>{
                    if(!nuevoServ.SERVICIO.trim()||!nuevoServ.COD_TIPO_SERVICIO)return;
                    setSavingServ(true);
                    try{
                      await api.post('/configuracion/tarifario/servicio',nuevoServ);
                      const r=await api.get('/configuracion/tarifario');
                      setTarifario(r.data.servicios||[]);
                      setShowNuevoServ(false);
                      showToast('✓ Servicio creado');
                    }catch(e){alert(e.response?.data?.error||e.message);}
                    setSavingServ(false);
                  }}>{savingServ?'Guardando...':'Guardar'}</button>
                  <button className="btn btn-sm" onClick={()=>setShowNuevoServ(false)}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Tabla con encabezado fijo y scroll propio — con listas largas (30+, 80+
                servicios) ya no hay que perder de vista las columnas al bajar. */}
            <div style={{maxHeight:520,overflowY:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'var(--surface)'}}>
                    {['Servicio','Tipo','Precio ($)','Aplica IVA','Precio modificable','Activo'].map(h=>(
                      <th key={h} style={{position:'sticky',top:0,zIndex:1,padding:'7px 14px',fontSize:10,fontWeight:600,
                        textTransform:'uppercase',letterSpacing:'.4px',color:'var(--text3)',background:'var(--surface)',
                        textAlign:h.includes('$')||h.includes('IVA')||h.includes('mod')?'center':'left',
                        borderBottom:'0.5px solid var(--border)'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((s,i)=>(
                    <tr key={s.COD} style={{borderBottom:'0.5px solid var(--border)',background:i%2?'transparent':'var(--surface-alt, transparent)'}}>
                      <td style={{padding:'10px 14px',fontWeight:500,fontSize:12}}>{s.SERVICIO}</td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{fontSize:10,padding:'2px 8px',borderRadius:4,
                          background:s.TIPO_SERVICIO?'var(--surface)':'var(--amber-lt)',
                          color:s.TIPO_SERVICIO?'var(--text3)':'var(--amber)'}}>
                          {s.TIPO_SERVICIO||'Sin tipo'}
                        </span>
                      </td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <input type="number" min="0" step="0.01"
                          style={{width:90,padding:'4px 8px',border:'0.5px solid var(--border)',
                            borderRadius:6,textAlign:'right',fontSize:12,
                            background:'var(--bg)',color:'var(--text1)'}}
                          value={s.VALOR||0}
                          onChange={e=>{
                            const v = parseFloat(e.target.value)||0;
                            setTarifario(t=>t.map(x=>x.COD===s.COD?{...x,VALOR:v}:x));
                          }}/>
                      </td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <input type="checkbox"
                          style={{width:16,height:16,cursor:'pointer'}}
                          checked={!!s.APLICA_IVA}
                          onChange={e=>setTarifario(t=>t.map(x=>x.COD===s.COD?{...x,APLICA_IVA:e.target.checked}:x))}/>
                      </td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <input type="checkbox"
                          style={{width:16,height:16,cursor:'pointer'}}
                          checked={s.VALOR_MODIFICABLE==='V'||s.VALOR_MODIFICABLE===true}
                          onChange={e=>setTarifario(t=>t.map(x=>x.COD===s.COD?{...x,VALOR_MODIFICABLE:e.target.checked?'V':'F'}:x))}/>
                      </td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <input type="checkbox"
                          style={{width:16,height:16,cursor:'pointer'}}
                          checked={s.ACTIVO==='V'}
                          onChange={e=>setTarifario(t=>t.map(x=>x.COD===s.COD?{...x,ACTIVO:e.target.checked?'V':'F'}:x))}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tarifario.length>0 && filtrados.length===0 && (
                <div style={{padding:24,textAlign:'center',color:'var(--text3)',fontSize:12}}>
                  Ningún servicio coincide con el filtro
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* ═══ EXÁMENES Y GRUPOS ═══════════════════════════════ */}
      {tab==='examenes'&&(
        <div style={{display:'grid',gridTemplateColumns:'250px 1fr',gap:14}}>
          {/* Grupos */}
          <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:640}}>
            <div style={{padding:'10px 14px',borderBottom:'0.5px solid var(--border)',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontSize:12,fontWeight:500}}>Grupos <span style={{color:'var(--text3)',fontWeight:400}}>({grupos.length})</span></span>
              <button className="btn btn-sm" style={{fontSize:11,padding:'2px 8px'}}
                onClick={()=>{setEditandoGrupoId(null);setFormGrupo({TIPO_DETERMINACION:'',C_FORMULARIO:'CLN',ORDEN_PRESENTACION:(grupos.length||0)+1});setShowFormGrupo(true);}}>
                + Nuevo
              </button>
            </div>
            <div style={{padding:'8px 14px',borderBottom:'0.5px solid var(--border)',flexShrink:0}}>
              <input className="form-control" style={{fontSize:12}} placeholder="Buscar grupo..."
                value={filtroGrupo} onChange={e=>setFiltroGrupo(e.target.value)}/>
            </div>
            {showFormGrupo&&(
              <div style={{padding:12,background:'var(--surface)',borderBottom:'0.5px solid var(--border)',flexShrink:0}}>
                <div style={{fontSize:11,fontWeight:500,marginBottom:8,color:'var(--text2)'}}>
                  {editandoGrupoId?'Editar grupo':'Nuevo grupo'}
                </div>
                <div className="form-group" style={{marginBottom:8}}>
                  <label className="form-label" style={{fontSize:10}}>Nombre del grupo</label>
                  <input className="form-control" style={{fontSize:12}} placeholder="Ej: Hematología"
                    value={formGrupo.TIPO_DETERMINACION}
                    onChange={e=>setFormGrupo(f=>({...f,TIPO_DETERMINACION:e.target.value}))}/>
                </div>
                <div className="form-group" style={{marginBottom:10}}>
                  <label className="form-label" style={{fontSize:10}}>Tipo de formulario</label>
                  <select className="form-control" style={{fontSize:12}} value={formGrupo.C_FORMULARIO}
                    onChange={e=>setFormGrupo(f=>({...f,C_FORMULARIO:e.target.value}))}>
                    <option value="CLN">Laboratorio</option>
                    <option value="IMG">Imagen</option>
                  </select>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-primary btn-sm" disabled={savingGrupo||!formGrupo.TIPO_DETERMINACION.trim()} onClick={async()=>{
                    if(!formGrupo.TIPO_DETERMINACION.trim())return;
                    setSavingGrupo(true);
                    try{
                      if(editandoGrupoId){
                        await api.put(`/laboratorio/config/grupos/${editandoGrupoId}`,formGrupo);
                        showToast('✓ Grupo actualizado');
                      }else{
                        await api.post('/laboratorio/config/grupos',formGrupo);
                        showToast('✓ Grupo creado');
                      }
                      const r=await api.get('/laboratorio/config/grupos');
                      setGrupos(r.data||[]);
                      setShowFormGrupo(false);
                      setEditandoGrupoId(null);
                    }catch(e){alert(e.response?.data?.error||e.message);}
                    setSavingGrupo(false);
                  }}>{savingGrupo?'Guardando...':'Guardar'}</button>
                  <button className="btn btn-sm" onClick={()=>{setShowFormGrupo(false);setEditandoGrupoId(null);}}>Cancelar</button>
                </div>
              </div>
            )}
            <div style={{overflowY:'auto',flex:1}}>
              {grupos.filter(g=>!filtroGrupo||(g.TIPO_DETERMINACION||'').toLowerCase().includes(filtroGrupo.toLowerCase())).length===0&&(
                <div style={{padding:20,textAlign:'center',color:'var(--text3)',fontSize:11}}>Sin grupos que coincidan</div>
              )}
              {grupos.filter(g=>!filtroGrupo||(g.TIPO_DETERMINACION||'').toLowerCase().includes(filtroGrupo.toLowerCase())).map(g=>{
                const sinNombre = !g.TIPO_DETERMINACION || !g.TIPO_DETERMINACION.trim() || g.TIPO_DETERMINACION.trim()==='.';
                return (
                <div key={g.COD} className="fila-grupo"
                  onClick={()=>{
                    setGrupoSel(g.COD);
                    api.get(`/laboratorio/config/examenes?grupo=${g.COD}`)
                      .then(r=>setExamList(r.data||[])).catch(()=>{});
                  }}
                  style={{padding:'8px 14px',cursor:'pointer',fontSize:12,
                    borderBottom:'0.5px solid var(--border)',
                    background:grupoSel===g.COD?'var(--blue-lt)':'',
                    display:'flex',alignItems:'center',gap:6}}>
                  <span style={{flex:1,fontStyle:sinNombre?'italic':'normal',color:sinNombre?'var(--red)':'inherit'}}>
                    {sinNombre?'(sin nombre — corrígelo)':g.TIPO_DETERMINACION}
                  </span>
                  <span style={{fontSize:10,padding:'1px 6px',borderRadius:4,
                    background:g.C_FORMULARIO==='IMG'?'#EEF6FF':'var(--green-lt)',
                    color:g.C_FORMULARIO==='IMG'?'#185FA5':'var(--green)'}}>
                    {g.C_FORMULARIO==='IMG'?'Imagen':'Lab'}
                  </span>
                  <span style={{fontSize:10,color:'var(--text3)',minWidth:14,textAlign:'right'}}>{g.total_examenes}</span>
                  <button className="btn-icon-grupo" title="Editar grupo" style={{fontSize:11,padding:'1px 5px',background:'transparent',border:'none',cursor:'pointer',color:'var(--text3)'}}
                    onClick={e=>{
                      e.stopPropagation();
                      setEditandoGrupoId(g.COD);
                      setFormGrupo({TIPO_DETERMINACION:g.TIPO_DETERMINACION||'',C_FORMULARIO:g.C_FORMULARIO||'CLN',ORDEN_PRESENTACION:g.ORDEN_PRESENTACION||1});
                      setShowFormGrupo(true);
                    }}>✏️</button>
                  <button title="Eliminar grupo" style={{fontSize:11,padding:'1px 5px',background:'transparent',border:'none',cursor:'pointer',color:'var(--text3)'}}
                    onClick={async e=>{
                      e.stopPropagation();
                      if(g.total_examenes>0){
                        alert(`No se puede eliminar: este grupo tiene ${g.total_examenes} examen(es). Muévelos o elimínalos primero.`);
                        return;
                      }
                      if(!window.confirm(`¿Eliminar el grupo "${g.TIPO_DETERMINACION||'(sin nombre)'}"?`))return;
                      try{
                        await api.delete(`/laboratorio/config/grupos/${g.COD}`);
                        const r=await api.get('/laboratorio/config/grupos');
                        setGrupos(r.data||[]);
                        if(grupoSel===g.COD){setGrupoSel('');setExamList([]);}
                        showToast('✓ Grupo eliminado');
                      }catch(err){alert(err.response?.data?.error||err.message);}
                    }}>🗑️</button>
                </div>
              );})}
            </div>
          </div>

          {/* Exámenes del grupo */}
          <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'10px 14px',borderBottom:'0.5px solid var(--border)',
              display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:12,fontWeight:500}}>
                {grupoSel?`Exámenes — ${(grupos.find(g=>g.COD===grupoSel)?.TIPO_DETERMINACION||'').trim()&&(grupos.find(g=>g.COD===grupoSel)?.TIPO_DETERMINACION||'').trim()!=='.'?grupos.find(g=>g.COD===grupoSel)?.TIPO_DETERMINACION:'(sin nombre)'}`:'Selecciona un grupo'}
              </span>
              {grupoSel&&(
                <button className="btn btn-sm btn-primary"
                  onClick={()=>{setFormEx({DETERMINACION:'',COD_TIPO_DETERMINACION:grupoSel,COD_SERVICIO:'',ORDEN_PRESENTACION:1});setShowFormEx(true);}}>
                  + Nuevo examen
                </button>
              )}
            </div>
            {showFormEx&&(
              <div style={{padding:12,background:'var(--surface)',borderBottom:'0.5px solid var(--border)'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                  <div>
                    <label className="form-label">Nombre del examen</label>
                    <input className="form-control" placeholder="Ej: Glucosa basal"
                      value={formEx.DETERMINACION}
                      onChange={e=>setFormEx(f=>({...f,DETERMINACION:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="form-label">Servicio / Precio</label>
                    <select className="form-control" value={formEx.COD_SERVICIO}
                      onChange={e=>setFormEx(f=>({...f,COD_SERVICIO:e.target.value}))}>
                      <option value="">Sin precio</option>
                      {tarifario.map(s=>(
                        <option key={s.COD} value={s.COD}>{s.SERVICIO} — ${Number(s.VALOR||0).toFixed(2)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <label style={{display:'flex',alignItems:'center',gap:7,fontSize:12,marginBottom:8,cursor:'pointer'}}>
                  <input type="checkbox" checked={!!formEx.ES_EXTERNO}
                    onChange={e=>setFormEx(f=>({...f,ES_EXTERNO:e.target.checked}))} style={{width:14,height:14,cursor:'pointer'}}/>
                  Examen externo (se envía a otro laboratorio)
                </label>
                {formEx.ES_EXTERNO&&(
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                    <div>
                      <label className="form-label" style={{fontSize:10}}>Laboratorio externo</label>
                      <input className="form-control" style={{fontSize:12}}
                        value={formEx.LABORATORIO_EXT||''} onChange={e=>setFormEx(f=>({...f,LABORATORIO_EXT:e.target.value}))}/>
                    </div>
                    <div>
                      <label className="form-label" style={{fontSize:10}}>Costo del laboratorio</label>
                      <input type="number" step="0.01" className="form-control" style={{fontSize:12}}
                        value={formEx.COSTO_EXTERNO||''} onChange={e=>setFormEx(f=>({...f,COSTO_EXTERNO:e.target.value}))}/>
                    </div>
                  </div>
                )}

                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-primary btn-sm" onClick={async()=>{
                    if(!formEx.DETERMINACION.trim())return;
                    try{
                      await api.post('/laboratorio/config/examenes',{
                        ...formEx,
                        ES_EXTERNO:formEx.ES_EXTERNO?'V':'F',
                        LABORATORIO_EXT:formEx.ES_EXTERNO?formEx.LABORATORIO_EXT:null,
                        COSTO_EXTERNO:formEx.ES_EXTERNO?(formEx.COSTO_EXTERNO||null):null,
                      });
                      const r=await api.get(`/laboratorio/config/examenes?grupo=${grupoSel}`);
                      setExamList(r.data||[]);setShowFormEx(false);showToast('✓ Examen creado');
                    }catch(e){alert(e.response?.data?.error||e.message);}
                  }}>Guardar</button>
                  <button className="btn btn-sm" onClick={()=>setShowFormEx(false)}>Cancelar</button>
                </div>
              </div>
            )}
            {!grupoSel&&<div style={{padding:32,textAlign:'center',color:'var(--text3)',fontSize:12}}>← Selecciona un grupo a la izquierda</div>}
            {grupoSel&&examList.length===0&&<div style={{padding:24,textAlign:'center',color:'var(--text3)',fontSize:12}}>No hay exámenes en este grupo</div>}
            {examList.map(ex=>{
              const expandido = examExp===ex.COD;
              const margen = (Number(extDatos.PRECIO_REF||ex.PRECIO||0) - Number(extDatos.COSTO_EXTERNO||0));
              return (
              <div key={ex.COD} style={{borderBottom:'0.5px solid var(--border)'}}>
                <div onClick={()=>{
                    if(expandido){ setExamExp(null); setLabCompleto(null); return; }
                    setExamExp(ex.COD);
                    setExtDatos({ ES_EXTERNO:ex.ES_EXTERNO==='V', LABORATORIO_EXT:ex.LABORATORIO_EXT||'',
                      COSTO_EXTERNO:ex.COSTO_EXTERNO||'', PRECIO_REF:ex.PRECIO });
                    setLabCompleto(null);
                    api.get(`/laboratorio/config/examenes/${ex.COD}/completo`)
                      .then(r=>setLabCompleto(r.data)).catch(()=>setLabCompleto({subdeterminaciones:[],rangos_normales:[],categoricos:[]}));
                  }}
                  style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',
                    padding:'8px 14px',fontSize:12,background:ex.ES_EXTERNO==='V'?'var(--amber-lt)':'transparent'}}>
                  <span style={{flex:1}}>{ex.DETERMINACION}</span>
                  <span style={{fontSize:11,color:ex.ES_EXTERNO==='V'?'var(--amber)':'var(--text3)'}}>
                    {ex.PRECIO>0?`$${Number(ex.PRECIO).toFixed(2)}`:'—'}
                    {ex.ES_EXTERNO==='V'&&ex.COSTO_EXTERNO!=null&&` · margen $${(Number(ex.PRECIO||0)-Number(ex.COSTO_EXTERNO||0)).toFixed(2)}`}
                  </span>
                  {ex.ES_EXTERNO==='V'&&<span style={{fontSize:10,padding:'1px 6px',borderRadius:4,background:'var(--amber)',color:'#fff'}}>Externo</span>}
                  <span style={{fontSize:10,padding:'1px 6px',borderRadius:4,
                    background:ex.ACTIVO==='V'?'var(--green-lt)':'var(--red-lt)',
                    color:ex.ACTIVO==='V'?'var(--green)':'var(--red)'}}>
                    {ex.ACTIVO==='V'?'Activo':'Inactivo'}
                  </span>
                  <i className={`ti ti-chevron-${expandido?'up':'down'}`} style={{fontSize:13,color:'var(--text3)'}} aria-hidden="true"></i>
                </div>

                {expandido&&(
                  <div style={{padding:14,background:'var(--bg)'}}>
                    <label style={{display:'flex',alignItems:'center',gap:7,fontSize:12,marginBottom:10,cursor:'pointer'}}>
                      <input type="checkbox" checked={!!extDatos.ES_EXTERNO}
                        onChange={e=>setExtDatos(d=>({...d,ES_EXTERNO:e.target.checked}))} style={{width:14,height:14,cursor:'pointer'}}/>
                      Examen externo (se envía a otro laboratorio)
                    </label>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
                      <div>
                        <label className="form-label" style={{fontSize:10}}>Laboratorio externo</label>
                        <input className="form-control" style={{fontSize:12}} disabled={!extDatos.ES_EXTERNO}
                          value={extDatos.LABORATORIO_EXT||''} onChange={e=>setExtDatos(d=>({...d,LABORATORIO_EXT:e.target.value}))}/>
                      </div>
                      <div>
                        <label className="form-label" style={{fontSize:10}}>Precio al paciente</label>
                        <input className="form-control" style={{fontSize:12,background:'var(--border)',color:'var(--text3)'}}
                          value={Number(ex.PRECIO||0).toFixed(2)} disabled/>
                        <div style={{fontSize:9.5,color:'var(--text3)',marginTop:2}}>se edita en Tarifario</div>
                      </div>
                      <div>
                        <label className="form-label" style={{fontSize:10}}>Costo del laboratorio</label>
                        <input type="number" step="0.01" className="form-control" style={{fontSize:12}} disabled={!extDatos.ES_EXTERNO}
                          value={extDatos.COSTO_EXTERNO||''} onChange={e=>setExtDatos(d=>({...d,COSTO_EXTERNO:e.target.value}))}/>
                      </div>
                    </div>
                    {extDatos.ES_EXTERNO&&(
                      <div style={{fontSize:12,color:margen>=0?'var(--green)':'var(--red)',
                        background:margen>=0?'var(--green-lt)':'var(--red-lt)',padding:'6px 10px',borderRadius:6,display:'inline-block',marginBottom:10}}>
                        Margen: ${margen.toFixed(2)}{ex.PRECIO>0&&` (${((margen/Number(ex.PRECIO))*100).toFixed(1)}%)`}
                      </div>
                    )}
                    <div style={{display:'flex',gap:8}}>
                      <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none'}}
                        disabled={extSaving}
                        onClick={async()=>{
                          setExtSaving(true);
                          try{
                            await api.put(`/laboratorio/config/examenes/${ex.COD}`,{
                              DETERMINACION:ex.DETERMINACION, COD_SERVICIO:ex.COD_SERVICIO,
                              ORDEN_PRESENTACION:ex.ORDEN_PRESENTACION, ACTIVO:ex.ACTIVO,
                              ES_EXTERNO:extDatos.ES_EXTERNO?'V':'F',
                              LABORATORIO_EXT:extDatos.ES_EXTERNO?extDatos.LABORATORIO_EXT:null,
                              COSTO_EXTERNO:extDatos.ES_EXTERNO?(extDatos.COSTO_EXTERNO||null):null,
                            });
                            const r=await api.get(`/laboratorio/config/examenes?grupo=${grupoSel}`);
                            setExamList(r.data||[]);
                            setExamExp(null);
                            showToast('✓ Guardado');
                          }catch(err){ showToast('Error: '+(err.response?.data?.error||err.message)); }
                          finally{ setExtSaving(false); }
                        }}>{extSaving?'Guardando...':'Guardar'}</button>
                      <button className="btn btn-sm" onClick={()=>setExamExp(null)}>Cancelar</button>
                    </div>

                    {/* ═══ Variables de laboratorio: subdeterminaciones y rangos ═══ */}
                    <div style={{borderTop:'0.5px solid var(--border)',marginTop:14,paddingTop:14}}>
                      <div style={{fontSize:12,fontWeight:500,marginBottom:10}}>🧬 Variables de laboratorio</div>
                      {!labCompleto&&<div style={{fontSize:11,color:'var(--text3)'}}>Cargando...</div>}
                      {labCompleto&&(
                        <>
                          {/* Sub-determinaciones */}
                          <div style={{marginBottom:14}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                              <span style={{fontSize:11,fontWeight:500,color:'var(--text2)'}}>Sub-determinaciones (ej. fórmula diferencial)</span>
                              <button className="btn btn-sm" style={{fontSize:10,padding:'2px 8px'}}
                                onClick={()=>{setShowNuevaSub(s=>!s);setSubExistente('');setNuevaSub({SUBDETERMINACION:'',SECCION:''});}}>
                                + Agregar
                              </button>
                            </div>
                            {showNuevaSub&&(
                              <div style={{background:'var(--surface)',border:'0.5px solid var(--border)',borderRadius:8,padding:10,marginBottom:8}}>
                                <div style={{display:'flex',gap:6,marginBottom:8,alignItems:'center'}}>
                                  <select className="form-control" style={{fontSize:11,flex:1}} value={subExistente}
                                    onChange={e=>setSubExistente(e.target.value)}>
                                    <option value="">— elegir del catálogo —</option>
                                    {catalogoSubdet
                                      .filter(s=>!labCompleto.subdeterminaciones.some(x=>x.COD===s.COD))
                                      .map(s=><option key={s.COD} value={s.COD}>{s.SUBDETERMINACION}{s.SECCION?` (${s.SECCION})`:''}</option>)}
                                  </select>
                                  <button className="btn btn-sm btn-primary" style={{fontSize:10,whiteSpace:'nowrap'}} disabled={!subExistente||savingLabVar}
                                    onClick={async()=>{
                                      setSavingLabVar(true);
                                      try{
                                        await api.post(`/laboratorio/config/examenes/${ex.COD}/subdeterminaciones`,{COD_SUBDETERMINACION:subExistente,OBLIGATORIO:'F'});
                                        const r=await api.get(`/laboratorio/config/examenes/${ex.COD}/completo`);
                                        setLabCompleto(r.data); setSubExistente(''); showToast('✓ Asociada');
                                      }catch(err){alert(err.response?.data?.error||err.message);}
                                      setSavingLabVar(false);
                                    }}>Asociar</button>
                                </div>
                                <div style={{fontSize:10,color:'var(--text3)',marginBottom:6}}>o crear una nueva:</div>
                                <div style={{display:'flex',gap:6}}>
                                  <input className="form-control" style={{fontSize:11,flex:1}} placeholder="Nombre (ej: Neutrófilos)"
                                    value={nuevaSub.SUBDETERMINACION} onChange={e=>setNuevaSub(f=>({...f,SUBDETERMINACION:e.target.value}))}/>
                                  <input className="form-control" style={{fontSize:11,width:110}} placeholder="Sección"
                                    value={nuevaSub.SECCION} onChange={e=>setNuevaSub(f=>({...f,SECCION:e.target.value}))}/>
                                  <button className="btn btn-sm btn-primary" style={{fontSize:10,whiteSpace:'nowrap'}}
                                    disabled={!nuevaSub.SUBDETERMINACION.trim()||savingLabVar}
                                    onClick={async()=>{
                                      setSavingLabVar(true);
                                      try{
                                        const {data:creada}=await api.post('/laboratorio/config/subdeterminaciones',nuevaSub);
                                        await api.post(`/laboratorio/config/examenes/${ex.COD}/subdeterminaciones`,{COD_SUBDETERMINACION:creada.COD,OBLIGATORIO:'F'});
                                        const [r,cat]=await Promise.all([
                                          api.get(`/laboratorio/config/examenes/${ex.COD}/completo`),
                                          api.get('/laboratorio/config/subdeterminaciones')
                                        ]);
                                        setLabCompleto(r.data); setCatalogoSubdet(cat.data||[]);
                                        setNuevaSub({SUBDETERMINACION:'',SECCION:''}); setShowNuevaSub(false);
                                        showToast('✓ Sub-determinación creada y asociada');
                                      }catch(err){alert(err.response?.data?.error||err.message);}
                                      setSavingLabVar(false);
                                    }}>Crear</button>
                                </div>
                              </div>
                            )}
                            {labCompleto.subdeterminaciones.length===0
                              ? <div style={{fontSize:11,color:'var(--text3)'}}>Sin sub-determinaciones</div>
                              : labCompleto.subdeterminaciones.map(s=>(
                                <div key={s.COD} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',fontSize:11,borderBottom:'0.5px solid var(--border)'}}>
                                  <span style={{flex:1}}>{s.SUBDETERMINACION}{s.SECCION?` · ${s.SECCION}`:''}</span>
                                  <button className="btn btn-sm" style={{fontSize:9,padding:'1px 6px'}}
                                    onClick={()=>{
                                      setRangoTarget({tipo:'CMP',cod_relacionado:s.COD,label:s.SUBDETERMINACION});
                                      setFormRango({C_SEXO:'I',EDAD_INFERIOR:0,EDAD_SUPERIOR:150,LIMITE_INFERIOR:0,LIMITE_SUPERIOR:0,COD_UNIDAD:'',OBSERVACIONES:''});
                                      setShowFormRango(true);
                                    }}>+ Rango</button>
                                  <button className="btn btn-sm" style={{fontSize:9,padding:'1px 6px',color:'var(--red)'}}
                                    onClick={async()=>{
                                      await api.delete(`/laboratorio/config/examenes/${ex.COD}/subdeterminaciones/${s.COD}`);
                                      const r=await api.get(`/laboratorio/config/examenes/${ex.COD}/completo`);
                                      setLabCompleto(r.data);
                                    }}>Quitar</button>
                                </div>
                              ))
                            }
                          </div>

                          {/* Rangos de normalidad del examen */}
                          <div style={{marginBottom:14}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                              <span style={{fontSize:11,fontWeight:500,color:'var(--text2)'}}>Rangos de normalidad</span>
                              <button className="btn btn-sm" style={{fontSize:10,padding:'2px 8px'}}
                                onClick={()=>{
                                  setRangoTarget({tipo:'DTR',cod_relacionado:ex.COD,label:ex.DETERMINACION});
                                  setFormRango({C_SEXO:'I',EDAD_INFERIOR:0,EDAD_SUPERIOR:150,LIMITE_INFERIOR:0,LIMITE_SUPERIOR:0,COD_UNIDAD:'',OBSERVACIONES:''});
                                  setShowFormRango(true);
                                }}>+ Rango</button>
                            </div>
                            {labCompleto.rangos_normales.length===0
                              ? <div style={{fontSize:11,color:'var(--text3)'}}>Sin rangos configurados</div>
                              : labCompleto.rangos_normales.map(r=>(
                                <div key={r.COD} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',fontSize:11,borderBottom:'0.5px solid var(--border)'}}>
                                  <span style={{width:52}}>{SEXO_LABEL[r.C_SEXO]||r.C_SEXO}</span>
                                  <span style={{width:90,color:'var(--text3)'}}>{r.EDAD_INFERIOR}–{r.EDAD_SUPERIOR} años</span>
                                  <span style={{flex:1}}>{r.LIMITE_INFERIOR} – {r.LIMITE_SUPERIOR} {r.UNIDAD||''}</span>
                                  <button className="btn btn-sm" style={{fontSize:9,padding:'1px 6px',color:'var(--red)'}}
                                    onClick={async()=>{
                                      await api.delete(`/laboratorio/config/rangos-normales/${r.COD}`);
                                      const rr=await api.get(`/laboratorio/config/examenes/${ex.COD}/completo`);
                                      setLabCompleto(rr.data);
                                    }}>Quitar</button>
                                </div>
                              ))
                            }
                          </div>

                          {/* Rangos categóricos */}
                          <div>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                              <span style={{fontSize:11,fontWeight:500,color:'var(--text2)'}}>Rangos categóricos (ej. Positivo/Negativo)</span>
                              <button className="btn btn-sm" style={{fontSize:10,padding:'2px 8px'}}
                                onClick={()=>{
                                  setRangoTarget({tipo:'CAT',cod_relacionado:ex.COD,label:ex.DETERMINACION});
                                  setFormCat({ETIQUETA:'',LIMITE_INFERIOR:0,LIMITE_SUPERIOR:0,COLOR:'#888888',C_SEXO:'I',EDAD_INFERIOR:0,EDAD_SUPERIOR:150});
                                  setShowFormCat(true);
                                }}>+ Categoría</button>
                            </div>
                            {labCompleto.categoricos.length===0
                              ? <div style={{fontSize:11,color:'var(--text3)'}}>Sin categorías configuradas</div>
                              : labCompleto.categoricos.map(c=>(
                                <div key={c.COD} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',fontSize:11,borderBottom:'0.5px solid var(--border)'}}>
                                  <span style={{width:12,height:12,borderRadius:3,background:c.COLOR,display:'inline-block',flexShrink:0}}></span>
                                  <span style={{flex:1}}>{c.ETIQUETA} ({c.LIMITE_INFERIOR}–{c.LIMITE_SUPERIOR})</span>
                                  <button className="btn btn-sm" style={{fontSize:9,padding:'1px 6px',color:'var(--red)'}}
                                    onClick={async()=>{
                                      await api.delete(`/laboratorio/config/rangos-categoricos/${c.COD}`);
                                      const rr=await api.get(`/laboratorio/config/examenes/${ex.COD}/completo`);
                                      setLabCompleto(rr.data);
                                    }}>Quitar</button>
                                </div>
                              ))
                            }
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      )}

      {/* ═══ MODAL: NUEVO RANGO DE NORMALIDAD ══════════════════ */}
      {showFormRango&&rangoTarget&&createPortal(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:9999,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>e.target===e.currentTarget&&setShowFormRango(false)}>
          <div style={{background:'var(--bg)',borderRadius:14,width:460,
            border:'0.5px solid var(--border)',margin:'0 16px'}}>
            <div style={{padding:'14px 20px',borderBottom:'0.5px solid var(--border)',
              display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:14,fontWeight:500}}>Nuevo rango — {rangoTarget.label}</span>
              <button className="btn btn-sm" onClick={()=>setShowFormRango(false)}>✕</button>
            </div>
            <div style={{padding:20,display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Sexo</label>
                  <select className="form-control" style={{fontSize:12}} value={formRango.C_SEXO}
                    onChange={e=>setFormRango(f=>({...f,C_SEXO:e.target.value}))}>
                    <option value="I">Ambos</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Edad desde</label>
                  <input type="number" className="form-control" style={{fontSize:12}} value={formRango.EDAD_INFERIOR}
                    onChange={e=>setFormRango(f=>({...f,EDAD_INFERIOR:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Edad hasta</label>
                  <input type="number" className="form-control" style={{fontSize:12}} value={formRango.EDAD_SUPERIOR}
                    onChange={e=>setFormRango(f=>({...f,EDAD_SUPERIOR:e.target.value}))}/>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Límite inferior</label>
                  <input type="number" step="0.01" className="form-control" style={{fontSize:12}} value={formRango.LIMITE_INFERIOR}
                    onChange={e=>setFormRango(f=>({...f,LIMITE_INFERIOR:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Límite superior</label>
                  <input type="number" step="0.01" className="form-control" style={{fontSize:12}} value={formRango.LIMITE_SUPERIOR}
                    onChange={e=>setFormRango(f=>({...f,LIMITE_SUPERIOR:e.target.value}))}/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" style={{fontSize:10}}>Unidad</label>
                <div style={{display:'flex',gap:6}}>
                  <select className="form-control" style={{fontSize:12,flex:1}} value={formRango.COD_UNIDAD}
                    onChange={e=>setFormRango(f=>({...f,COD_UNIDAD:e.target.value}))}>
                    <option value="">Sin unidad</option>
                    {unidadesLab.map(u=><option key={u.COD} value={u.COD}>{u.UNIDAD}{u.DESCRIPCION?` — ${u.DESCRIPCION}`:''}</option>)}
                  </select>
                  <button className="btn btn-sm" style={{fontSize:11,whiteSpace:'nowrap'}}
                    onClick={()=>setShowNuevaUnidadEnRango(s=>!s)}>+ Unidad</button>
                </div>
                {showNuevaUnidadEnRango&&(
                  <div style={{display:'flex',gap:6,marginTop:6}}>
                    <input className="form-control" style={{fontSize:11,flex:1}} placeholder="Ej: mg/dL"
                      value={nuevaUnidad.UNIDAD} onChange={e=>setNuevaUnidad(u=>({...u,UNIDAD:e.target.value}))}/>
                    <input className="form-control" style={{fontSize:11,flex:1}} placeholder="Descripción (opcional)"
                      value={nuevaUnidad.DESCRIPCION} onChange={e=>setNuevaUnidad(u=>({...u,DESCRIPCION:e.target.value}))}/>
                    <button className="btn btn-sm btn-primary" style={{fontSize:11,whiteSpace:'nowrap'}}
                      disabled={!nuevaUnidad.UNIDAD.trim()}
                      onClick={async()=>{
                        try{
                          const {data}=await api.post('/laboratorio/config/unidades',nuevaUnidad);
                          const r=await api.get('/laboratorio/config/unidades');
                          setUnidadesLab(r.data||[]);
                          setFormRango(f=>({...f,COD_UNIDAD:data.COD}));
                          setNuevaUnidad({C_AGRUPADOR:'GEN',UNIDAD:'',DESCRIPCION:''});
                          setShowNuevaUnidadEnRango(false);
                        }catch(err){alert(err.response?.data?.error||err.message);}
                      }}>Crear</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" style={{fontSize:10}}>Observaciones (opcional)</label>
                <input className="form-control" style={{fontSize:12}} value={formRango.OBSERVACIONES}
                  onChange={e=>setFormRango(f=>({...f,OBSERVACIONES:e.target.value}))}/>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:6}}>
                <button className="btn btn-sm" onClick={()=>setShowFormRango(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" disabled={savingLabVar}
                  onClick={async()=>{
                    setSavingLabVar(true);
                    try{
                      await api.post('/laboratorio/config/rangos-normales',{
                        C_TIPO_RELACIONADO_RANGO_NORMAL: rangoTarget.tipo,
                        COD_RELACIONADO: rangoTarget.cod_relacionado,
                        COD_UNIDAD: formRango.COD_UNIDAD||null,
                        C_SEXO: formRango.C_SEXO,
                        EDAD_INFERIOR: formRango.EDAD_INFERIOR,
                        EDAD_SUPERIOR: formRango.EDAD_SUPERIOR,
                        LIMITE_INFERIOR: formRango.LIMITE_INFERIOR,
                        LIMITE_SUPERIOR: formRango.LIMITE_SUPERIOR,
                        OBSERVACIONES: formRango.OBSERVACIONES,
                        usuario: usuario?.usuario,
                      });
                      if(examExp){
                        const r=await api.get(`/laboratorio/config/examenes/${examExp}/completo`);
                        setLabCompleto(r.data);
                      }
                      setShowFormRango(false);
                      showToast('✓ Rango guardado');
                    }catch(err){alert(err.response?.data?.error||err.message);}
                    setSavingLabVar(false);
                  }}>{savingLabVar?'Guardando...':'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ MODAL: NUEVA CATEGORÍA (rango categórico) ═════════ */}
      {showFormCat&&createPortal(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:9999,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>e.target===e.currentTarget&&setShowFormCat(false)}>
          <div style={{background:'var(--bg)',borderRadius:14,width:420,
            border:'0.5px solid var(--border)',margin:'0 16px'}}>
            <div style={{padding:'14px 20px',borderBottom:'0.5px solid var(--border)',
              display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:14,fontWeight:500}}>Nueva categoría</span>
              <button className="btn btn-sm" onClick={()=>setShowFormCat(false)}>✕</button>
            </div>
            <div style={{padding:20,display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',gap:8}}>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label" style={{fontSize:10}}>Etiqueta</label>
                  <input className="form-control" style={{fontSize:12}} placeholder="Ej: Positivo"
                    value={formCat.ETIQUETA} onChange={e=>setFormCat(f=>({...f,ETIQUETA:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Color</label>
                  <input type="color" className="form-control" style={{fontSize:12,width:52,padding:2}}
                    value={formCat.COLOR} onChange={e=>setFormCat(f=>({...f,COLOR:e.target.value}))}/>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Límite inferior</label>
                  <input type="number" step="0.01" className="form-control" style={{fontSize:12}} value={formCat.LIMITE_INFERIOR}
                    onChange={e=>setFormCat(f=>({...f,LIMITE_INFERIOR:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Límite superior</label>
                  <input type="number" step="0.01" className="form-control" style={{fontSize:12}} value={formCat.LIMITE_SUPERIOR}
                    onChange={e=>setFormCat(f=>({...f,LIMITE_SUPERIOR:e.target.value}))}/>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Sexo</label>
                  <select className="form-control" style={{fontSize:12}} value={formCat.C_SEXO}
                    onChange={e=>setFormCat(f=>({...f,C_SEXO:e.target.value}))}>
                    <option value="I">Ambos</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Edad desde</label>
                  <input type="number" className="form-control" style={{fontSize:12}} value={formCat.EDAD_INFERIOR}
                    onChange={e=>setFormCat(f=>({...f,EDAD_INFERIOR:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:10}}>Edad hasta</label>
                  <input type="number" className="form-control" style={{fontSize:12}} value={formCat.EDAD_SUPERIOR}
                    onChange={e=>setFormCat(f=>({...f,EDAD_SUPERIOR:e.target.value}))}/>
                </div>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:6}}>
                <button className="btn btn-sm" onClick={()=>setShowFormCat(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" disabled={savingLabVar||!formCat.ETIQUETA.trim()}
                  onClick={async()=>{
                    setSavingLabVar(true);
                    try{
                      await api.post('/laboratorio/config/rangos-categoricos',{
                        COD_DETERMINACION: rangoTarget?.cod_relacionado,
                        ...formCat,
                      });
                      if(examExp){
                        const r=await api.get(`/laboratorio/config/examenes/${examExp}/completo`);
                        setLabCompleto(r.data);
                      }
                      setShowFormCat(false);
                      showToast('✓ Categoría guardada');
                    }catch(err){alert(err.response?.data?.error||err.message);}
                    setSavingLabVar(false);
                  }}>{savingLabVar?'Guardando...':'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ PLANTILLAS DE CONSULTA ════════════════════════ */}
      {tab==='plantillas'&&(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontSize:12,color:'var(--text2)'}}>
              Plantillas de <strong>{usuario?.nombres}</strong>
            </div>
            <button className="btn btn-primary btn-sm"
              onClick={()=>{setFormPl({NOMBRE:'',DESCRIPCION:'',EVOLUCION:'',diagnosticos:[]});setEditPl(null);setShowFormPl(true);}}>
              + Nueva plantilla
            </button>
          </div>

          {/* Formulario nueva/editar plantilla */}
          {showFormPl&&(
            <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,padding:14}}>
              <div style={{fontSize:12,fontWeight:500,marginBottom:10}}>
                {editPl?'Editar plantilla':'Nueva plantilla'}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <div>
                  <label className="form-label">Nombre</label>
                  <input className="form-control" placeholder="Ej: Control hipotiroidismo"
                    value={formPl.NOMBRE} onChange={e=>setFormPl(f=>({...f,NOMBRE:e.target.value}))}/>
                </div>
                <div>
                  <label className="form-label">Descripción (opcional)</label>
                  <input className="form-control" placeholder="Descripción breve"
                    value={formPl.DESCRIPCION} onChange={e=>setFormPl(f=>({...f,DESCRIPCION:e.target.value}))}/>
                </div>
              </div>
              <div style={{marginBottom:8}}>
                <label className="form-label">Texto de evolución</label>
                <textarea className="form-control" rows={4}
                  placeholder="Motivo de consulta, examen físico, plan terapéutico..."
                  value={formPl.EVOLUCION} onChange={e=>setFormPl(f=>({...f,EVOLUCION:e.target.value}))}
                  style={{resize:'vertical'}}/>
              </div>
              <div style={{marginBottom:8}}>
                <label className="form-label">Diagnósticos CIE-10</label>
                <div style={{position:'relative',marginBottom:6}}>
                  <input className="form-control" placeholder="Buscar CIE-10..."
                    value={busqCie} onChange={async e=>{
                      setBusqCie(e.target.value);
                      if(e.target.value.length>1){
                        try{const{data}=await api.get(`/historia/cie10?q=${encodeURIComponent(e.target.value)}`);setSugsCie(data||[]);}catch{}
                      } else setSugsCie([]);
                    }}/>
                  {sugsCie.length>0&&(
                    <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg)',
                      border:'0.5px solid var(--border)',borderRadius:8,zIndex:50,maxHeight:180,overflowY:'auto',marginTop:4}}>
                      {sugsCie.map(c=>(
                        <div key={c.COD} style={{padding:'6px 10px',cursor:'pointer',fontSize:12,
                          borderBottom:'0.5px solid var(--border)'}}
                          onMouseEnter={e=>e.currentTarget.style.background='var(--surface)'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}
                          onClick={()=>{
                            setFormPl(f=>({...f,diagnosticos:[...f.diagnosticos,{COD_CIE10:c.COD,DESCRIPCION_CIE:c.DESCRIPCION}]}));
                            setBusqCie('');setSugsCie([]);
                          }}>
                          <span style={{color:'var(--navy-lt)',fontWeight:500,marginRight:8}}>{c.COD}</span>
                          {c.DESCRIPCION}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formPl.diagnosticos.map((d,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 8px',
                    background:'var(--surface)',borderRadius:6,marginBottom:4,fontSize:12}}>
                    <span style={{color:'var(--navy-lt)',fontWeight:500}}>{d.COD_CIE10}</span>
                    <span style={{flex:1}}>{d.DESCRIPCION_CIE}</span>
                    <button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:14}}
                      onClick={()=>setFormPl(f=>({...f,diagnosticos:f.diagnosticos.filter((_,j)=>j!==i)}))}>×</button>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-sm" onClick={()=>setShowFormPl(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" onClick={async()=>{
                  if(!formPl.NOMBRE||!formPl.EVOLUCION){alert('Nombre y evolución son requeridos');return;}
                  try{
                    if(editPl){
                      await api.put(`/historia/plantillas-consulta/${editPl}`,{...formPl,usuario:usuario?.usuario});
                    } else {
                      await api.post('/historia/plantillas-consulta',{...formPl,usuario:usuario?.usuario});
                    }
                    const r=await api.get(`/historia/plantillas-consulta?medico=${usuario?.usuario}`);
                    setPlantillasC(r.data||[]);
                    setShowFormPl(false);showToast('✓ Plantilla guardada');
                  }catch(e){alert(e.response?.data?.error||e.message);}
                }}>Guardar</button>
              </div>
            </div>
          )}

          {/* Lista de plantillas */}
          {plantillasC.length===0&&!showFormPl&&(
            <div style={{padding:32,textAlign:'center',color:'var(--text3)',fontSize:12}}>
              No tienes plantillas creadas — crea la primera
            </div>
          )}
          {plantillasC.map(p=>(
            <div key={p.COD} style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,padding:12}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500}}>{p.NOMBRE}</div>
                  {p.DESCRIPCION&&<div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{p.DESCRIPCION}</div>}
                  <div style={{fontSize:11,color:'var(--text2)',marginTop:6,lineHeight:1.5,
                    maxHeight:60,overflow:'hidden',textOverflow:'ellipsis'}}>
                    {p.EVOLUCION}
                  </div>
                  {p.diagnosticos?.length>0&&(
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:6}}>
                      {p.diagnosticos.map(d=>(
                        <span key={d.COD_CIE10} style={{fontSize:10,padding:'1px 7px',borderRadius:4,
                          background:'var(--blue-lt)',color:'var(--navy-lt)'}}>
                          {d.COD_CIE10} — {d.DESCRIPCION_CIE}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  <button className="btn btn-sm" onClick={()=>{
                    setFormPl({NOMBRE:p.NOMBRE,DESCRIPCION:p.DESCRIPCION||'',
                      EVOLUCION:p.EVOLUCION,diagnosticos:p.diagnosticos||[]});
                    setEditPl(p.COD);setShowFormPl(true);
                  }}>✏️ Editar</button>
                  <button className="btn btn-sm" style={{color:'var(--red)'}}
                    onClick={async()=>{
                      if(!window.confirm('¿Eliminar esta plantilla?'))return;
                      await api.delete(`/historia/plantillas-consulta/${p.COD}`);
                      setPlantillasC(pp=>pp.filter(x=>x.COD!==p.COD));
                    }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ CONSENTIMIENTOS INFORMADOS ═══════════════════════ */}
      {tab==='consentimientos'&&(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:13,fontWeight:500}}>Plantillas de consentimiento informado</span>
            <button className="btn btn-primary btn-sm" onClick={()=>{
              setFormConsent({TITULO:'',CUERPO:'',REQUIERE_FIRMA_MEDICO:false,REQUIERE_FIRMA_TESTIGO:false});
              setEditConsentId(null); setShowFormConsent(true);
            }}>+ Nueva plantilla</button>
          </div>

          {showFormConsent&&(
            <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,padding:16}}>
              <div style={{marginBottom:10}}>
                <label className="form-label">Título</label>
                <input className="form-control" placeholder="Ej: Consentimiento para Cirugía Menor"
                  value={formConsent.TITULO} onChange={e=>setFormConsent(f=>({...f,TITULO:e.target.value}))}/>
              </div>
              <div style={{marginBottom:10}}>
                <label className="form-label">Cuerpo del documento (texto legal completo)</label>
                <textarea className="form-control" rows={10} style={{fontFamily:'inherit',fontSize:12,lineHeight:1.5}}
                  placeholder="Separar secciones con una línea en blanco. Las líneas que empiecen con un número (Ej: '1. Identificación...') se muestran como subtítulo."
                  value={formConsent.CUERPO} onChange={e=>setFormConsent(f=>({...f,CUERPO:e.target.value}))}/>
              </div>
              <div style={{display:'flex',gap:16,marginBottom:12}}>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={formConsent.REQUIERE_FIRMA_MEDICO}
                    onChange={e=>setFormConsent(f=>({...f,REQUIERE_FIRMA_MEDICO:e.target.checked}))} style={{width:14,height:14,cursor:'pointer'}}/>
                  Requiere firma del médico
                </label>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={formConsent.REQUIERE_FIRMA_TESTIGO}
                    onChange={e=>setFormConsent(f=>({...f,REQUIERE_FIRMA_TESTIGO:e.target.checked}))} style={{width:14,height:14,cursor:'pointer'}}/>
                  Requiere firma de testigo
                </label>
              </div>
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-primary btn-sm" disabled={savingConsent} onClick={async()=>{
                  if(!formConsent.TITULO.trim()||!formConsent.CUERPO.trim())return;
                  setSavingConsent(true);
                  try{
                    if(editConsentId) await api.put(`/historia/plantillas-consentimiento/${editConsentId}`,formConsent);
                    else await api.post('/historia/plantillas-consentimiento',formConsent);
                    const r=await api.get('/historia/plantillas-consentimiento');
                    setPlantillasConsent(r.data||[]);
                    setShowFormConsent(false);
                    showToast(editConsentId?'✓ Plantilla actualizada':'✓ Plantilla creada');
                  }catch(e){alert(e.response?.data?.error||e.message);}
                  setSavingConsent(false);
                }}>{savingConsent?'Guardando...':'Guardar'}</button>
                <button className="btn btn-sm" onClick={()=>setShowFormConsent(false)}>Cancelar</button>
              </div>
            </div>
          )}

          {plantillasConsent.length===0&&!showFormConsent&&(
            <div style={{padding:24,textAlign:'center',color:'var(--text3)',fontSize:12}}>Sin plantillas de consentimiento creadas</div>
          )}
          {plantillasConsent.map(p=>(
            <div key={p.COD} style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,padding:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>{p.TITULO}</div>
                  <div style={{display:'flex',gap:6,marginTop:4}}>
                    {p.REQUIERE_FIRMA_MEDICO==='V'&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:4,background:'var(--blue-lt)',color:'var(--navy)'}}>Firma médico</span>}
                    {p.REQUIERE_FIRMA_TESTIGO==='V'&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:4,background:'var(--amber-lt)',color:'var(--amber)'}}>Firma testigo</span>}
                  </div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-sm" style={{fontSize:11}} onClick={()=>{
                    setFormConsent({TITULO:p.TITULO,CUERPO:p.CUERPO,
                      REQUIERE_FIRMA_MEDICO:p.REQUIERE_FIRMA_MEDICO==='V',REQUIERE_FIRMA_TESTIGO:p.REQUIERE_FIRMA_TESTIGO==='V'});
                    setEditConsentId(p.COD); setShowFormConsent(true);
                  }}>✏️ Editar</button>
                  <button className="btn btn-sm" style={{fontSize:11,color:'var(--red)'}} onClick={async()=>{
                    if(!window.confirm('¿Eliminar esta plantilla?'))return;
                    await api.delete(`/historia/plantillas-consentimiento/${p.COD}`);
                    setPlantillasConsent(pp=>pp.filter(x=>x.COD!==p.COD));
                  }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ ASISTENTE IA ═══════════════════════════════════ */}
      {tab==='ia'&&(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>

          {/* Estado actual */}
          <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,padding:14}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
              <div style={{width:44,height:44,borderRadius:'50%',background:'var(--blue-lt)',
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>🤖</div>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>Asistente IA para consultas médicas</div>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>
                  Graba la consulta médico-paciente y genera automáticamente la evolución y diagnósticos CIE-10
                </div>
              </div>
              <span style={{marginLeft:'auto',fontSize:11,padding:'3px 10px',borderRadius:20,fontWeight:500,
                background:iaCfg.configurado?'var(--green-lt)':'var(--surface)',
                color:iaCfg.configurado?'var(--green)':'var(--text3)'}}>
                {iaCfg.configurado?'✓ Configurado':'No configurado'}
              </span>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
              {[['🎙️ Whisper','Transcripción de voz a texto en español'],
                ['🧠 GPT-4o mini','Análisis médico y generación de borrador'],
                ['🔒 Privacidad','El audio no se almacena en OpenAI'],
                ['💰 Costo','~$0.01–0.05 USD por consulta']
              ].map(([icon,txt])=>(
                <div key={icon} style={{background:'var(--surface)',borderRadius:8,padding:'8px 10px',
                  display:'flex',gap:8,alignItems:'flex-start'}}>
                  <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
                  <span style={{fontSize:11,color:'var(--text2)'}}>{txt}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Configurar API Key */}
          <div style={{background:'var(--bg)',border:'0.5px solid var(--border)',borderRadius:10,padding:14}}>
            <div style={{fontSize:12,fontWeight:500,marginBottom:10}}>Configurar API Key de OpenAI</div>
            <div style={{fontSize:11,color:'var(--text3)',marginBottom:10}}>
              Obtén tu API Key en <span style={{color:'var(--navy-lt)'}}>platform.openai.com</span> → API Keys → Create new secret key
            </div>
            <div style={{display:'flex',gap:8,marginBottom:10}}>
              <div style={{flex:1,position:'relative'}}>
                <input className="form-control"
                  type={showKey?'text':'password'}
                  placeholder="sk-proj-..."
                  value={iaCfg.api_key}
                  onChange={e=>setIaCfg(c=>({...c,api_key:e.target.value}))}
                  style={{paddingRight:36}}/>
                <button type="button"
                  style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                    background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--text3)'}}
                  onClick={()=>setShowKey(s=>!s)}>
                  {showKey?'🙈':'👁️'}
                </button>
              </div>
              <button className="btn btn-primary"
                disabled={savingIA||!iaCfg.api_key.startsWith('sk-')}
                onClick={async()=>{
                  setSavingIA(true);
                  try{
                    await api.post('/historia/ia-config',{api_key:iaCfg.api_key});
                    setIaCfg(c=>({...c,configurado:true}));
                    showToast('✓ API Key guardada correctamente');
                  }catch(e){alert(e.response?.data?.error||e.message);}
                  setSavingIA(false);
                }}>
                {savingIA?'Guardando...':'Guardar'}
              </button>
            </div>
            {iaCfg.configurado&&(
              <div style={{fontSize:11,color:'var(--green)',background:'var(--green-lt)',
                padding:'6px 10px',borderRadius:6}}>
                ✓ API Key configurada. El asistente IA está activo en el módulo de Historia Clínica.
              </div>
            )}
            {!iaCfg.configurado&&(
              <div style={{fontSize:11,color:'var(--text3)',background:'var(--surface)',
                padding:'6px 10px',borderRadius:6}}>
                Sin API Key configurada. El botón de micrófono no aparecerá en las consultas.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
